import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    Plus,
    Wallet,
    AlertTriangle,
    Trash2,
    MoreHorizontal,
    Inbox,
    Image as ImageIcon,
    Download,
    Pencil,
    Check,
    X as XIcon,
    Users,
    Shield,
    CheckSquare,
    Square,
    ChevronDown,
    ChevronRight,
    MoreVertical,
    Repeat,
    ArrowUp,
    ArrowDown,
    List as ListIcon,
    PieChart,
    FileText,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import PanelShell from './PanelShell';
import expenseService from '../../services/expenseService';
import { debounce } from '../../utils/debounce';
import { removeExpense, renameBook, updateExpense } from '../../utils/financeLiveState';
import { bookTotals } from '../../utils/bookTotals';
import { documentService } from '../../services/documentService';
import useDocumentBlobUrl from '../../hooks/useDocumentBlobUrl';
import useCurrencyPreference from '../../hooks/useCurrencyPreference';
import useFittedFigure from '../../hooks/useFittedFigure';
import useMenuDismiss from '../../hooks/useMenuDismiss';
import useAuth from '../../hooks/useAuth';
import { formatUploadedAt } from '../../utils/datetime';
import { useLanguage } from '../../i18n';
import { getAuthToken } from '../../utils/authToken';
import DocumentPreviewModal from '../DocumentPreviewModal';
import { isPdfContentType } from '../../utils/receiptKind';
import SharedSpaceDialog from '../SharedSpaceDialog';
import Modal from '../ui/Modal';
import FinanceCharts from '../finance/FinanceCharts';
import AttachedFileChoice, { hasAttachedFile, LinkedFilesChoice } from '../finance/AttachedFileChoice';
import { httpError, userErrorMessage, userFacingError } from '../../utils/userError';

const UNASSIGNED_KEY = 'unassigned';
const LAST_BOOK_STORAGE_KEY = 'omnis:expenses:lastBookKey';
// Book "Options" pop-over box (Options layout finance #249) — the width is the
// real w-56 (14rem) so the placement math can keep BOTH edges on screen; the
// height is a rough worst case (6 rows + separators) used only to decide
// open-up vs open-down, so the box is always fully framed.
const OPTIONS_MENU_WIDTH = 224;
const OPTIONS_MENU_EST_HEIGHT = 260;
const OPTIONS_MENU_GAP = 4;   // trigger ↔ menu
const OPTIONS_MENU_EDGE = 8;  // minimum breathing room against any viewport edge

// Recurring / "Fixed" transactions (#190) — the 5 UI frequency options (mirror
// recurrence_service.UI_FREQUENCIES on the BE). Custom intervals ("every 27
// days") are set via PAI only, per the manual-entry asterisk note.
const RECURRENCE_OPTIONS = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'every_3_months', label: 'Every 3 Months' },
    { value: 'every_6_months', label: 'Every 6 Months' },
    { value: 'yearly', label: 'Yearly' },
];
const recurrenceLabel = (recurrence, intervalDays) => {
    if (!recurrence) return null;
    if (recurrence === 'custom') return intervalDays ? `Every ${intervalDays} days` : 'Custom';
    return RECURRENCE_OPTIONS.find((o) => o.value === recurrence)?.label
        || String(recurrence).replace(/_/g, ' ');
};

// ── Recurring 2.0 (#234): count the occurrences the lazy materializer will
// backfill for a chosen Starting Date, matching recurrence_service.
// materialize_due_recurrences EXACTLY (so the retroactive prompt's N is honest):
//   • generation starts AT the chosen Starting Date (the anchor day itself is the
//     first generated occurrence);
//   • SOURCE-AWARE (#234 re-open #3): ANY scheduled date equal to the
//     transaction's OWN date is suppressed — the source ticket already IS that
//     date's entry. This covers both the anchor == transaction date case and a
//     PAST anchor whose schedule later walks onto the source date
//     (Jan 3 → … → Jun 3), which used to emit a twin of the source.
// All math is on UTC-midnight Dates parsed from YYYY-MM-DD (no tz drift).
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
const parseYMD = (s) => {
    if (!s) return null;
    const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(Date.UTC(y, m - 1, d));
};
const todayUTC = () => {
    const n = new Date();
    return new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate()));
};
const addMonthsUTC = (dt, months) => {
    const m0 = dt.getUTCMonth() + months;
    const y2 = dt.getUTCFullYear() + Math.floor(m0 / 12);
    const m2 = ((m0 % 12) + 12) % 12;
    const lastDay = new Date(Date.UTC(y2, m2 + 1, 0)).getUTCDate(); // clamp Jan31+1mo→Feb28
    return new Date(Date.UTC(y2, m2, Math.min(dt.getUTCDate(), lastDay)));
};
const advanceUTC = (dt, freq) => {
    switch (freq) {
        case 'weekly': return new Date(dt.getTime() + 7 * 86400000);
        case 'monthly': return addMonthsUTC(dt, 1);
        case 'every_3_months': return addMonthsUTC(dt, 3);
        case 'every_6_months': return addMonthsUTC(dt, 6);
        case 'yearly': return addMonthsUTC(dt, 12);
        default: return null;
    }
};
const countBackfillOccurrences = (anchorStr, expenseDateStr, freq) => {
    const anchor = parseYMD(anchorStr);
    if (!anchor || !freq) return 0;
    const expDate = parseYMD(expenseDateStr);
    const today = todayUTC();
    let cursor = anchor;
    let n = 0;
    let steps = 0;
    while (cursor && cursor.getTime() <= today.getTime() && n < 400 && steps <= 400) {
        steps += 1;
        // Source-Aware: the source ticket covers its own date — nothing generated.
        if (!(expDate && cursor.getTime() === expDate.getTime())) n += 1;
        cursor = advanceUTC(cursor, freq);
    }
    return n;
};
const monthName = (anchorStr) => {
    const d = parseYMD(anchorStr);
    return d ? MONTH_NAMES[d.getUTCMonth()] : '';
};
// Friendly DD/MM/YYYY for the recurrence-impact "preserved source" line (#234).
const formatSourceDate = (ymd) => {
    const d = parseYMD(ymd);
    if (!d) return String(ymd || '');
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
};

const readLastBookKey = () => {
    try { return localStorage.getItem(LAST_BOOK_STORAGE_KEY) || null; } catch (_) { return null; }
};
const writeLastBookKey = (key) => {
    try {
        if (key) localStorage.setItem(LAST_BOOK_STORAGE_KEY, key);
    } catch (_) { /* ignore */ }
};
// Shared dataTransfer types — `text/expense-id` keeps backward-compatibility
// with the book-tab drop targets inside the panel itself; the richer JSON
// payload under `application/x-expense-ref` is consumed by ChatInput so the
// reference chip can carry merchant/amount/book context.
const DRAG_TYPE_ID = 'text/expense-id';
const DRAG_TYPE_REF = 'application/x-expense-ref';

// ── Semantic row tinting — the core Finance pattern (PAI design system) ───────
// The product is monochrome until a value carries meaning: a ledger row is the
// canonical place an accent is allowed, and it may only tint a 1px border and a
// background wash at <=12% alpha. Expense = red #F04438, income = green #34D058,
// resting wash 0.04 / border 0.32, deepening to 0.09 on hover. Declared ONCE so
// the two card states (normal + selection mode) can never drift apart.
const ROW_TINT = {
    expense: 'border-[rgba(240,68,56,0.32)] bg-[rgba(240,68,56,0.04)]',
    income: 'border-[rgba(52,208,88,0.32)] bg-[rgba(52,208,88,0.04)]',
};
const ROW_TINT_HOVER = {
    expense: 'hover:bg-[rgba(240,68,56,0.09)]',
    income: 'hover:bg-[rgba(52,208,88,0.09)]',
};
const rowKind = (expense) => (expense?.direction === 'income' ? 'income' : 'expense');

const formatAmount = (amount, currency = 'USD') => {
    if (amount == null) return '—';
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency || 'USD',
            maximumFractionDigits: 2,
        }).format(amount);
    } catch (_) {
        return `${currency || 'USD'} ${Number(amount).toFixed(2)}`;
    }
};

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    } catch (_) {
        return iso;
    }
};

const FALLBACK_CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'THB', 'CHF', 'CAD', 'AUD'];

const buildEditForm = (expense) => ({
    merchant: expense.merchant || '',
    amount: expense.amount != null ? String(expense.amount) : '',
    currency: expense.currency || 'USD',
    category: expense.category || '',
    expense_date: expense.expense_date ? String(expense.expense_date).slice(0, 10) : '',
    description: expense.description || '',
});

// ── "Fixed Expense/Income" recurrence dialog (#190 + Recurring 2.0 #234) ──────
// Opened from the ticket kebab menu. Layout: Starting Date (top) → Frequency
// (below). Save PATCHes {recurrence, recurrence_anchor_date, occurrence_cleanup?}.
//
// Recurring 2.0 adds three confirmation bubbles on top of the plain save:
//   • Substitution — editing an EXISTING rule's frequency/start date → Confirm &
//     Replace (delete old generated entries + regenerate) / Keep Both (leave the
//     old ones as independent rows, still apply the new schedule) / Cancel.
//   • Retroactive — a Starting Date in the PAST → warns it will backfill N missing
//     entries from <Month> until today (N matches the materializer exactly).
//   • Stop cleanup — Stop recurring → Eliminate all generated entries / Keep them
//     as individual entries.
// `expense` is always the RULE-OWNING row (the template). Recurring 2.0 (#234
// re-open) — "Universal Recurrence Control": a lineage occurrence resolves its
// template before opening this dialog, so Stop recurring / the impact fetch /
// the PATCH all target the RULE, never the generated copy the user clicked.
// `fromOccurrence` only drives the scope notice.
const RecurrenceDialog = ({ expense, onSave, onClose, fromOccurrence = false }) => {
    const { t } = useLanguage();
    const isIncome = expense.direction === 'income';
    // Capture the rule state at OPEN so we can tell an edit from a first-time set.
    const wasRecurring = useMemo(() => !!expense.recurrence, []); // eslint-disable-line react-hooks/exhaustive-deps
    const initialFrequency = useMemo(
        () => (expense.recurrence && expense.recurrence !== 'custom' ? expense.recurrence : ''),
        [], // eslint-disable-line react-hooks/exhaustive-deps
    );
    const initialAnchor = useMemo(
        () => (expense.recurrence_anchor_date && String(expense.recurrence_anchor_date).slice(0, 10))
            || (expense.expense_date && String(expense.expense_date).slice(0, 10))
            || new Date().toISOString().slice(0, 10),
        [], // eslint-disable-line react-hooks/exhaustive-deps
    );
    const [frequency, setFrequency] = useState(initialFrequency);
    const [anchor, setAnchor] = useState(initialAnchor);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // stage: 'form' | 'substitution' | 'retro' | 'stop'
    const [stage, setStage] = useState('form');
    const [pendingCleanup, setPendingCleanup] = useState(null);
    // #234 re-open: the eliminate IMPACT (whole-lineage count + preserved source),
    // fetched when the Stop-cleanup bubble opens so the user sees exactly how many
    // generated entries Eliminate will wipe and which original ticket is kept.
    const [impact, setImpact] = useState(null);
    const [impactLoading, setImpactLoading] = useState(false);

    useEffect(() => {
        if (stage !== 'stop') return undefined;
        let cancelled = false;
        setImpactLoading(true);
        expenseService.recurrenceImpact(expense.id)
            .then((r) => { if (!cancelled) setImpact(r); })
            .catch(() => { if (!cancelled) setImpact(null); })
            .finally(() => { if (!cancelled) setImpactLoading(false); });
        return () => { cancelled = true; };
    }, [stage, expense.id]);

    const ruleChanged = wasRecurring
        && (frequency !== initialFrequency || anchor !== initialAnchor);

    // Retroactive backfill preview — honest count matching the BE materializer.
    const retro = useMemo(() => {
        const anchorD = parseYMD(anchor);
        if (!anchorD || !frequency || anchorD.getTime() >= todayUTC().getTime()) {
            return { show: false, n: 0, month: '' };
        }
        const n = countBackfillOccurrences(anchor, expense.expense_date, frequency);
        return { show: n >= 1, n, month: monthName(anchor) };
    }, [anchor, frequency, expense.expense_date]);

    const doSave = async (cleanup) => {
        setSaving(true);
        setError(null);
        try {
            const payload = { recurrence: frequency, recurrence_anchor_date: anchor || null };
            if (cleanup) payload.occurrence_cleanup = cleanup;
            await onSave(payload);
            onClose();
        } catch (e) {
            setError(userErrorMessage(e, t));
            setSaving(false);
            setStage('form');
        }
    };

    const doStop = async (mode) => {
        setSaving(true);
        setError(null);
        try {
            await onSave({ recurrence: 'none', occurrence_cleanup: mode });
            onClose();
        } catch (e) {
            setError(userErrorMessage(e, t));
            setSaving(false);
            setStage('form');
        }
    };

    // After a substitution choice (or a first-time set), fall through to the
    // retroactive confirm when the Starting Date is in the past — else save now.
    const proceed = (cleanup) => {
        if (retro.show) { setPendingCleanup(cleanup); setStage('retro'); } else { doSave(cleanup); }
    };

    const onSaveClick = () => {
        if (!frequency) { setError(t('finPickFrequency', 'Pick a frequency.')); return; }
        setError(null);
        if (ruleChanged) { setStage('substitution'); return; }
        if (!wasRecurring) { proceed(null); return; }
        // Existing rule, nothing changed → nothing regenerates: just save.
        doSave(null);
    };

    const HeaderRow = (
        <div className="flex items-center justify-between p-5 border-b border-pai-subtle">
            <h3 className="text-[17px] font-bold text-white flex items-center gap-2.5">
                <Repeat size={16} className="text-pai-meta" />
                {isIncome ? t('finFixedIncome', 'Fixed Income') : t('finFixedExpense', 'Fixed Expense')}
            </h3>
            <button onClick={onClose} className="pai-icon-btn w-[34px] h-[34px]">
                <XIcon size={16} />
            </button>
        </div>
    );

    return (
        <Modal onClose={onClose} zIndex="z-[85]" size="sm" cardTestId="recurrence-dialog">
                {HeaderRow}

                {stage === 'form' && (
                    <>
                        <div className="p-5 space-y-4">
                            {fromOccurrence && (
                                <p
                                    data-testid="recurrence-rule-scope"
                                    className="text-[12.5px] text-pai-blue border border-[rgba(74,144,240,0.32)] bg-[rgba(74,144,240,0.05)] rounded-ctl px-3 py-2"
                                >
                                    {t('finManagingRuleScope', 'You’re managing the recurring rule behind this entry — changes apply to the whole series.')}
                                </p>
                            )}
                            <p className="text-[13.5px] text-pai-meta">
                                {t('finMakeRepeat', 'Make “{merchant}” repeat automatically — each occurrence is logged for you.', { merchant: expense.merchant || t('finThisTransaction', 'this transaction') })}
                            </p>
                            {/* Recurring 2.0 layout: Starting Date on TOP, Frequency underneath.
                                recurring-2-0.spec.js measures both boundingBoxes and asserts
                                anchor.y < frequency.y — keep them stacked, never side by side. */}
                            <label className="block">
                                <span className="text-[12.5px] font-medium text-pai-secondary">
                                    {t('finStartingDateLabel', 'Starting date for recurring ticket')}
                                </span>
                                <input
                                    type="date"
                                    data-testid="recurrence-anchor-date"
                                    value={anchor}
                                    onChange={(e) => setAnchor(e.target.value)}
                                    className="pai-input mt-1.5 w-full px-3 h-[42px] text-sm [color-scheme:dark]"
                                />
                            </label>
                            <label className="block">
                                <span className="text-[12.5px] font-medium text-pai-secondary">{t('finFrequencyLabel', 'Frequency')}</span>
                                <select
                                    data-testid="recurrence-frequency"
                                    value={frequency}
                                    onChange={(e) => setFrequency(e.target.value)}
                                    className="pai-input mt-1.5 w-full px-3 h-[42px] text-sm [color-scheme:dark]"
                                >
                                    <option value="">{t('finSelectFrequency', 'Select a frequency…')}</option>
                                    {RECURRENCE_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </label>
                            <p className="text-[12.5px] text-pai-faint">
                                {t('finRecurrenceNote', '* If these options do not match your recurring time, ask PAI in the main chat or via Telegram to set your specific schedule (e.g., “Make this repeat every 27 days”).')}
                            </p>
                            {error && <div className="text-[13px] text-pai-red">{error}</div>}
                        </div>

                        <div className="px-5 py-4 border-t border-pai-subtle flex items-center justify-between gap-2">
                            {wasRecurring ? (
                                <button
                                    data-testid="recurrence-stop"
                                    onClick={() => setStage('stop')}
                                    disabled={saving}
                                    className="text-[13.5px] font-semibold text-pai-red hover:text-white transition-colors disabled:opacity-50"
                                >
                                    {t('finStopRecurring', 'Stop recurring')}
                                </button>
                            ) : <span />}
                            <div className="flex gap-2.5">
                                <button
                                    onClick={onClose}
                                    disabled={saving}
                                    className="pai-btn-ghost disabled:opacity-50"
                                >
                                    {t('commonCancel', 'Cancel')}
                                </button>
                                <button
                                    data-testid="recurrence-save"
                                    onClick={onSaveClick}
                                    disabled={saving}
                                    className="pai-btn-primary disabled:opacity-50"
                                >
                                    {saving ? t('finSavingEllipsis', 'Saving…') : t('commonSave', 'Save')}
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {stage === 'substitution' && (
                    <div data-testid="recurrence-substitution-bubble" className="p-5 space-y-4">
                        <p className="text-[15px] leading-[1.6] text-pai-body">
                            {t('finSubstitutionQuestion', 'You’re changing an existing recurring rule. What should happen to the entries already generated by the old schedule?')}
                        </p>
                        <div className="flex flex-col gap-2.5">
                            <button
                                data-testid="recurrence-replace"
                                onClick={() => proceed('replace')}
                                disabled={saving}
                                className="w-full px-4 py-3 text-left text-sm font-semibold text-white rounded-ctl border border-pai bg-white/[0.05] hover:bg-white/[0.1] hover:border-white/30 transition-colors disabled:opacity-50"
                            >
                                {t('finConfirmReplace', 'Confirm & Replace')}
                                <span className="block mt-1 text-[12.5px] font-normal text-pai-meta">{t('finConfirmReplaceDesc', 'Remove the old generated entries and use the new schedule.')}</span>
                            </button>
                            <button
                                data-testid="recurrence-keep-both"
                                onClick={() => proceed('keep_both')}
                                disabled={saving}
                                className="w-full px-4 py-3 text-left text-sm font-semibold text-white rounded-ctl border border-pai-subtle bg-white/[0.025] hover:bg-white/[0.07] transition-colors disabled:opacity-50"
                            >
                                {t('finKeepBoth', 'Keep Both')}
                                <span className="block mt-1 text-[12.5px] font-normal text-pai-meta">{t('finKeepBothDesc', 'Keep the previously generated entries and also apply the new schedule.')}</span>
                            </button>
                            <button
                                data-testid="recurrence-cancel"
                                onClick={() => setStage('form')}
                                disabled={saving}
                                className="w-full px-4 py-2 text-sm font-semibold text-pai-meta hover:text-white transition-colors rounded-ctl disabled:opacity-50"
                            >
                                {t('commonCancel', 'Cancel')}
                            </button>
                        </div>
                        {error && <div className="text-[13px] text-pai-red">{error}</div>}
                    </div>
                )}

                {stage === 'retro' && (
                    <div data-testid="recurrence-retro-bubble" className="p-5 space-y-4">
                        <p className="text-[15px] leading-[1.6] text-pai-body">
                            {t('finRetroConfirm', 'I’ve detected a start date in the past. I am going to generate the {n} missing {entryWord} from {month} until today. Do you confirm this?', { n: retro.n, entryWord: retro.n === 1 ? t('finEntrySingular', 'entry') : t('finEntryPlural', 'entries'), month: retro.month })}
                        </p>
                        <div className="flex justify-end gap-2.5">
                            <button
                                data-testid="recurrence-retro-cancel"
                                onClick={() => setStage('form')}
                                disabled={saving}
                                className="pai-btn-ghost disabled:opacity-50"
                            >
                                {t('commonCancel', 'Cancel')}
                            </button>
                            <button
                                data-testid="recurrence-retro-confirm"
                                onClick={() => doSave(pendingCleanup)}
                                disabled={saving}
                                className="pai-btn-primary disabled:opacity-50"
                            >
                                {saving ? t('finSavingEllipsis', 'Saving…') : t('commonConfirm', 'Confirm')}
                            </button>
                        </div>
                        {error && <div className="text-[13px] text-pai-red">{error}</div>}
                    </div>
                )}

                {stage === 'stop' && (
                    <div data-testid="recurrence-cleanup-bubble" className="p-5 space-y-4">
                        <p className="text-[15px] leading-[1.6] text-pai-body">
                            {t('finRuleStoppedQuestion', 'Rule stopped. Do you want to eliminate all generated duplicates from the ledger, or keep them as individual entries?')}
                        </p>
                        {impactLoading ? (
                            <p className="text-[13px] text-pai-meta">{t('finCheckingEntries', 'Checking generated entries…')}</p>
                        ) : impact ? (
                            <div
                                data-testid="recurrence-cleanup-impact"
                                className="text-[13px] text-pai-secondary border border-pai-subtle bg-white/[0.025] rounded-ctl p-3 space-y-1.5"
                            >
                                <p>
                                    {t('finEliminateWillPart1', 'This will eliminate')} <strong className="tnum text-white">{impact.occurrence_count}</strong>{' '}
                                    {t('finEliminateWillPart2', 'generated {entryWord} across the whole rule’s history.', { entryWord: impact.occurrence_count === 1 ? t('finEntrySingular', 'entry') : t('finEntryPlural', 'entries') })}
                                </p>
                                <p>
                                    {t('finKeepingOriginalTicket', 'Keeping your original ticket:')}{' '}
                                    <strong className="text-white">{impact.merchant || t('finThisTransaction', 'this transaction')}</strong>
                                    {impact.expense_date ? <span className="tnum">{` — ${formatSourceDate(impact.expense_date)}`}</span> : ''}.
                                </p>
                            </div>
                        ) : null}
                        <div className="flex flex-col gap-2.5">
                            <button
                                data-testid="recurrence-cleanup-eliminate"
                                onClick={() => doStop('eliminate')}
                                disabled={saving}
                                className="pai-btn-danger w-full disabled:opacity-50"
                            >
                                {t('finEliminateGeneratedEntries', 'Eliminate generated entries')}
                            </button>
                            <button
                                data-testid="recurrence-cleanup-keep"
                                onClick={() => doStop('keep')}
                                disabled={saving}
                                className="pai-btn-secondary w-full disabled:opacity-50"
                            >
                                {t('finKeepIndividualEntries', 'Keep them as individual entries')}
                            </button>
                            <button
                                onClick={() => setStage('form')}
                                disabled={saving}
                                className="w-full px-4 py-2 text-sm font-semibold text-pai-meta hover:text-white transition-colors rounded-ctl disabled:opacity-50"
                            >
                                {t('commonCancel', 'Cancel')}
                            </button>
                        </div>
                        {error && <div className="text-[13px] text-pai-red">{error}</div>}
                    </div>
                )}
            </Modal>
    );
};

const ExpenseCard = ({
    expense,
    books,
    supportedCurrencies = FALLBACK_CURRENCIES,
    onAssign,
    onDelete,
    onUpdate,
    onDragStart,
    onDragEnd,
    isDragging,
    focused = false,
    canModify = true,
    selectionMode = false,
    selected = false,
    onSelectToggle,
    onOpen,
    onPhotoOpen,
    onResolveTemplate,
}) => {
    const { t } = useLanguage();
    // #481: the figure is keyed on this text (see `netText` in ExpensesPanel).
    const amountText = `${expense.direction === 'income' ? '+' : ''}${formatAmount(expense.amount, expense.currency)}`;
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null); // {top, right} in viewport coords
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    // Portal-anchored: pin the menu's right edge to the trigger's right edge so it
    // always escapes the card's column. null = the trigger is gone or off screen.
    const measureMenuAnchor = () => {
        const r = triggerRef.current?.getBoundingClientRect();
        if (!r || r.bottom < 0 || r.top > window.innerHeight) return null;
        return { top: Math.round(r.bottom + 4), right: Math.max(8, Math.round(window.innerWidth - r.right)) };
    };
    // #500 re-open: no full-screen click-catcher (it swallowed every wheel/touch
    // and locked the page). Outside press closes; a page scroll moves the menu
    // with its trigger, and closes it once the trigger leaves the screen.
    useMenuDismiss(menuOpen, () => setMenuOpen(false), [triggerRef, menuRef], () => {
        const next = measureMenuAnchor();
        if (!next) return false;
        setMenuAnchor((prev) => (prev && prev.top === next.top && prev.right === next.right ? prev : next));
        return true;
    });
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState(() => buildEditForm(expense));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // #190: kebab "Move Expense/Income" submenu toggle + the "Fixed" recurrence dialog.
    const [moveOpen, setMoveOpen] = useState(false);
    // Recurring 2.0 "Universal Recurrence Control" (#234 re-open): the dialog is
    // always opened on the RULE-OWNING row, which for a generated occurrence is
    // its template — so `recurrenceTarget` holds that resolved row, not always
    // this card's own expense.
    const [recurrenceTarget, setRecurrenceTarget] = useState(null);
    const [recurrenceLoading, setRecurrenceLoading] = useState(false);
    const cardRef = useRef(null);
    const needsClarification = expense.status === 'needs_clarification';
    const kindLabel = expense.direction === 'income' ? t('finIncomeWord', 'Income') : t('finExpenseWord', 'Expense');

    // Which row owns the recurrence rule for this card?
    //   • a template            → itself
    //   • an attached occurrence → recurrence_parent_id
    //   • a DETACHED keep_both row whose root template is still alive
    //                           → recurrence_root_id
    //   • a plain / orphaned row → itself (nothing to manage; it can be made
    //     recurring from scratch, which is the pre-existing behaviour)
    const ruleId = expense.is_recurring
        ? expense.id
        : (expense.recurrence_parent_id || expense.recurrence_root_id || expense.id);
    const isLineageChild = ruleId !== expense.id;

    useEffect(() => {
        if (focused && cardRef.current) {
            // Defer slightly so the panel has finished rendering its tabs/list.
            const t = setTimeout(() => {
                cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 80);
            return () => clearTimeout(t);
        }
    }, [focused, expense.id]);
    const bookName = useMemo(() => {
        if (!expense.book_id) return null;
        return books.find((b) => b.id === expense.book_id)?.name || null;
    }, [expense.book_id, books]);

    // #190 "Move Expense/Income" — ONLY the OTHER books (never the current one),
    // plus "Unassigned" as a destination when the row is currently in a book.
    const moveTargets = useMemo(() => {
        const targets = books
            .filter((b) => b.id !== expense.book_id)
            .map((b) => ({ key: b.id, id: b.id, name: b.name }));
        if (expense.book_id) targets.push({ key: '__unassigned', id: null, name: t('finUnassigned', 'Unassigned') });
        return targets;
    }, [books, expense.book_id, t]);

    // When the underlying expense changes (e.g. another save replaced it),
    // reset the form draft so we never show stale values.
    useEffect(() => {
        if (!editing) setForm(buildEditForm(expense));
    }, [expense, editing]);

    const startEdit = () => {
        setForm(buildEditForm(expense));
        setError(null);
        setEditing(true);
    };

    // Recurring 2.0 "Universal Recurrence Control" (#234 re-open). The rule lives
    // on the template, so a lineage occurrence resolves it BEFORE opening the
    // dialog — otherwise the dialog saw `recurrence: null` on the copy, hid
    // "Stop recurring" entirely, and its Save PATCHed an occurrence id, which the
    // BE deliberately refuses. Resolution is a lookup in the already-loaded
    // ledger first (occurrences inherit the template's book, so it is normally
    // right there), with an account-wide lookup only as a fallback. If the rule
    // can't be resolved we fall back to this row, which then behaves exactly as
    // it did before (an independent transaction that can be made recurring).
    const openRecurrence = async () => {
        setMenuOpen(false);
        setMoveOpen(false);
        if (!isLineageChild) {
            setRecurrenceTarget(expense);
            return;
        }
        setRecurrenceLoading(true);
        try {
            const tpl = await onResolveTemplate?.(ruleId);
            setRecurrenceTarget(tpl?.is_recurring ? tpl : expense);
        } catch {
            setRecurrenceTarget(expense);
        } finally {
            setRecurrenceLoading(false);
        }
    };
    const cancelEdit = () => {
        setEditing(false);
        setError(null);
        setForm(buildEditForm(expense));
    };

    const updateField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

    const handleSave = async () => {
        if (!onUpdate) return;
        setSaving(true);
        setError(null);
        try {
            const updates = {};
            // Always include all fields so the user can clear them.
            updates.merchant = form.merchant.trim() || null;
            updates.category = form.category.trim() || null;
            updates.description = form.description.trim() || null;
            updates.currency = (form.currency || 'USD').toUpperCase();
            // Amount: must be a finite number; treat empty as null
            const amountStr = form.amount.trim();
            if (amountStr === '') {
                updates.amount = null;
            } else {
                const parsed = Number(amountStr);
                if (!Number.isFinite(parsed)) {
                    throw userFacingError(t('finAmountMustBeNumber', 'Amount must be a number.'));
                }
                updates.amount = parsed;
            }
            // #510: a transaction's date can be changed, never removed.
            if (!form.expense_date) {
                throw userFacingError(t('finDateRequired', 'Date is required — every transaction needs a date.'));
            }
            updates.expense_date = form.expense_date;
            // If the row was flagged for clarification and the user has now
            // filled in a merchant, clear the flag so the warning disappears.
            if (needsClarification && updates.merchant) {
                updates.status = 'confirmed';
                updates.clarification_reason = null;
            }
            await onUpdate(expense.id, updates);
            setEditing(false);
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <div className="pai-glow border-[1.5px] rounded-row overflow-hidden flex">
                <div className="flex-1 p-3.5 min-w-0 space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                        <span className="pai-eyebrow">
                            {t('finEditingExpense', 'Editing expense')}
                        </span>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={cancelEdit}
                                disabled={saving}
                                className="pai-icon-btn w-7 h-7 disabled:opacity-50"
                                title={t('commonCancel', 'Cancel')}
                            >
                                <XIcon size={14} />
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="pai-icon-btn w-7 h-7 text-pai-green hover:text-pai-green disabled:opacity-50"
                                title={t('finSaveEnterTitle', 'Save (Enter)')}
                            >
                                <Check size={14} />
                            </button>
                        </div>
                    </div>

                    <input
                        type="text"
                        value={form.merchant}
                        onChange={(e) => updateField('merchant', e.target.value)}
                        placeholder={t('finMerchantPlaceholder', 'Merchant')}
                        autoFocus
                        className="pai-input w-full px-2.5 py-1.5 text-sm"
                    />

                    <div className="grid grid-cols-[1fr_auto] gap-2">
                        <input
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            value={form.amount}
                            onChange={(e) => updateField('amount', e.target.value)}
                            placeholder={t('commonAmount', 'Amount')}
                            className="pai-input tnum w-full px-2.5 py-1.5 text-sm"
                        />
                        <select
                            value={form.currency}
                            onChange={(e) => updateField('currency', e.target.value)}
                            className="pai-input px-2.5 py-1.5 text-sm [color-scheme:dark]"
                        >
                            {supportedCurrencies.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="text"
                            value={form.category}
                            onChange={(e) => updateField('category', e.target.value)}
                            placeholder={t('commonCategory', 'Category')}
                            className="pai-input w-full px-2.5 py-1.5 text-sm"
                        />
                        <input
                            type="date"
                            value={form.expense_date}
                            onChange={(e) => updateField('expense_date', e.target.value)}
                            className="pai-input tnum w-full px-2.5 py-1.5 text-sm [color-scheme:dark]"
                        />
                    </div>

                    <textarea
                        value={form.description}
                        onChange={(e) => updateField('description', e.target.value)}
                        placeholder={t('commonDescription', 'Description')}
                        rows={2}
                        className="pai-input w-full px-2.5 py-1.5 text-sm resize-none"
                    />

                    {error && (
                        <div className="text-[12.5px] text-pai-red">{error}</div>
                    )}
                </div>
            </div>
        );
    }

    if (selectionMode) {
        // Selection mode — strip drag handlers, swallow card clicks for
        // toggle, hide the more-menu. Backend will reject any operation
        // the user can't perform; we just hide the toggle for view-only.
        const isToggleable = canModify;
        return (
            <div
                ref={cardRef}
                onClick={() => isToggleable && onSelectToggle?.(expense.id)}
                className={`border rounded-row overflow-hidden transition-colors flex select-none ${
                    ROW_TINT[rowKind(expense)]
                } ${
                    selected ? 'border-[1.5px] border-white/80 shadow-glow' : ''
                } ${
                    isToggleable ? `cursor-pointer ${ROW_TINT_HOVER[rowKind(expense)]}` : 'opacity-50 cursor-not-allowed'
                }`}
            >
                <div className="flex items-center justify-center w-9 flex-shrink-0 border-r border-pai-hairline">
                    {selected ? (
                        <CheckSquare size={16} className="text-white" />
                    ) : (
                        <Square size={16} className={isToggleable ? 'text-pai-meta' : 'text-pai-faint'} />
                    )}
                </div>
                <div className="flex-1 p-3.5 min-w-0">
                    <div className="flex items-baseline gap-2 min-w-0">
                        <span key={amountText} translate="no" className={`tnum text-[16px] font-extrabold flex-shrink-0 ${
                            expense.direction === 'income' ? 'text-pai-green' : 'text-white'
                        }`}>
                            {amountText}
                        </span>
                        <span className="text-[13.5px] font-medium text-pai-secondary truncate min-w-0">
                            {expense.merchant || t('finUnknownMerchant', 'Unknown merchant')}
                        </span>
                    </div>
                    <div className="mt-1 tnum text-[12.5px] text-pai-meta">
                        {formatDate(expense.expense_date)}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={cardRef}
            data-testid="expense-card"
            data-expense-id={expense.id}
            data-has-document={expense.document_id ? 'true' : 'false'}
            draggable
            onDragStart={(e) => {
                e.dataTransfer.setData(DRAG_TYPE_ID, expense.id);
                // Rich payload for chat-input drop target (reference chip)
                const refPayload = {
                    expense_id: expense.id,
                    direction: expense.direction || 'expense',
                    amount: expense.amount,
                    currency: expense.currency || 'USD',
                    merchant: expense.merchant || null,
                    category: expense.category || null,
                    expense_date: expense.expense_date || null,
                    description: expense.description || null,
                    book_id: expense.book_id || null,
                    book_name: bookName,
                    document_id: expense.document_id || null,
                };
                e.dataTransfer.setData(DRAG_TYPE_REF, JSON.stringify(refPayload));
                e.dataTransfer.effectAllowed = 'copyMove';
                try {
                    window.dispatchEvent(new CustomEvent('omnis:expense-drag-start', { detail: refPayload }));
                } catch (_) { /* ignore */ }
                onDragStart?.(expense);
            }}
            onDragEnd={() => {
                try {
                    window.dispatchEvent(new CustomEvent('omnis:expense-drag-end'));
                } catch (_) { /* ignore */ }
                onDragEnd?.(expense);
            }}
            onClick={(e) => {
                // Clicking anywhere on the card opens the Focus Overlay,
                // EXCEPT for buttons / menus / the photo thumb (which have
                // their own handlers that stopPropagation()).
                if (e.defaultPrevented) return;
                onOpen?.(expense);
            }}
            className={`border rounded-row transition-colors flex cursor-pointer ${
                ROW_TINT[rowKind(expense)]
            } ${ROW_TINT_HOVER[rowKind(expense)]} ${
                focused
                    ? 'border-[1.5px] border-white/80 shadow-glow animate-pulse-once'
                    : ''
            } ${
                isDragging ? 'opacity-40 scale-[0.98]' : ''
            } cursor-grab active:cursor-grabbing`}
        >
            <div className="flex-1 p-3.5 min-w-0">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                        <div key={amountText} translate="no" className={`tnum text-[18px] font-extrabold tracking-[-0.02em] ${
                            expense.direction === 'income' ? 'text-pai-green' : 'text-white'
                        }`}>
                            {amountText}
                        </div>
                        <div className="mt-1 text-[14.5px] font-semibold text-white truncate">
                            {expense.merchant || t('finUnknownMerchant', 'Unknown merchant')}
                        </div>
                        {expense.category && (
                            <div className="mt-1.5 inline-block text-[11.5px] font-medium px-2.5 py-0.5 rounded-full border border-pai-strong text-pai-secondary">
                                {expense.category}
                            </div>
                        )}
                        <div className="mt-1.5 tnum text-[12.5px] text-pai-meta">
                            {formatDate(expense.expense_date)}
                        </div>
                        {/* #190: recurring "Fixed" template chip / auto-occurrence marker.
                            The design's one badge idiom — a tinted 1px outline pill, never
                            an accent-filled chip (the "Auto-recurring" badge in the handoff).
                            #508: an auto-generated occurrence shows the SAME green chip with
                            its series' frequency (`series_recurrence`, from the list route) —
                            it used to be a faint "Auto (recurring)" line. The testids stay
                            distinct (template vs occurrence) for the specs. */}
                        {(expense.is_recurring || expense.recurrence_parent_id) && (() => {
                            const isOccurrence = !expense.is_recurring;
                            const freq = isOccurrence
                                ? recurrenceLabel(expense.series_recurrence, expense.series_recurrence_interval_days)
                                : recurrenceLabel(expense.recurrence, expense.recurrence_interval_days);
                            return (
                                <div
                                    data-testid={isOccurrence ? 'recurring-occurrence' : 'recurring-chip'}
                                    className="mt-1.5 inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1 rounded-full border border-[rgba(52,208,88,0.5)] text-pai-green"
                                    title={isOccurrence ? t('finAutoGeneratedTitle', 'Auto-generated from a recurring transaction') : undefined}
                                >
                                    <Repeat size={11} /> {t('finRecurringPrefix', 'Recurring')}{freq ? ` · ${freq}` : ''}
                                </div>
                            );
                        })()}
                        {expense.description && (
                            <div className="mt-1.5 text-[12.5px] text-pai-meta line-clamp-2">
                                {expense.description}
                            </div>
                        )}
                        {expense.document_id && (
                            <div className="mt-2">
                                <TicketImageButton
                                    documentId={expense.document_id}
                                    filename={expense.merchant ? `${expense.merchant}-${expense.expense_date || ''}` : 'ticket'}
                                    onPreview={() => onPhotoOpen?.(expense)}
                                />
                            </div>
                        )}
                        {needsClarification && (
                            <div className="mt-2 flex items-start gap-1.5 text-[12.5px] text-pai-amber">
                                <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                                <span>{expense.clarification_reason || t('finNeedsClarification', 'Needs clarification')}</span>
                            </div>
                        )}
                    </div>
                    {!canModify ? (
                        <span
                            className="text-[10.5px] font-bold uppercase tracking-[0.07em] px-2 py-1 rounded-chip border border-pai-strong text-pai-meta flex-shrink-0"
                            title={t('finViewOnlyTitle', 'Created by another member — only owners or the original creator can change this')}
                        >
                            {t('finViewOnly', 'view only')}
                        </span>
                    ) : (
                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                        <button
                            ref={triggerRef}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (menuOpen) {
                                    setMenuOpen(false);
                                    return;
                                }
                                const anchor = measureMenuAnchor();
                                if (anchor) setMenuAnchor(anchor);
                                setMenuOpen(true);
                            }}
                            className="pai-icon-btn w-7 h-7 rounded-chip"
                            title={t('finMoreTitle', 'More')}
                        >
                            <MoreHorizontal size={16} />
                        </button>
                        {menuOpen && menuAnchor && createPortal(
                            <>
                                <div
                                    ref={menuRef}
                                    data-testid="ticket-menu"
                                    style={{ position: 'fixed', top: menuAnchor.top, right: menuAnchor.right }}
                                    className="w-56 max-w-[calc(100vw-16px)] bg-pai-modal border border-pai rounded-ctl shadow-modal-sm z-[70] py-1.5"
                                >
                                    <button
                                        onClick={() => { setMenuOpen(false); startEdit(); }}
                                        className="w-full text-left px-3.5 py-2 text-sm text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors flex items-center gap-2.5"
                                    >
                                        <Pencil size={12} />
                                        {t('finEditDetails', 'Edit details')}
                                    </button>
                                    {/* #190: Fixed Expense/Income — make the transaction recurring.
                                        #234 re-open: on a generated occurrence this opens the
                                        rule of the SERIES (its template), so "Stop recurring" is
                                        reachable from EVERY ticket in the lineage. */}
                                    <button
                                        data-testid="ticket-menu-fixed"
                                        onClick={openRecurrence}
                                        disabled={recurrenceLoading}
                                        className="w-full text-left px-3.5 py-2 text-sm text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors flex items-center gap-2.5 disabled:opacity-60"
                                    >
                                        <Repeat size={13} />
                                        <span className="flex-1">
                                            {isLineageChild ? t('finRecurrenceRule', 'Recurrence rule') : t('finFixedKind', 'Fixed {kind}', { kind: kindLabel })}
                                        </span>
                                        {expense.is_recurring && (
                                            <span className="text-[11px] font-semibold text-pai-green">
                                                {recurrenceLabel(expense.recurrence, expense.recurrence_interval_days)}
                                            </span>
                                        )}
                                    </button>
                                    <div className="border-t border-pai-hairline my-1.5" />
                                    {/* #190: Move Expense/Income — the OTHER books only. #500: the list
                                        opens in its own <Modal> window instead of expanding inside this
                                        fixed, viewport-anchored menu, which had no height limit — past ~4
                                        books the rest ran off-screen with no way to scroll to them. */}
                                    <button
                                        data-testid="ticket-menu-move"
                                        onClick={() => { setMenuOpen(false); setMoveOpen(true); }}
                                        className="w-full text-left px-3.5 py-2 text-sm text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors flex items-center gap-2.5"
                                    >
                                        <ChevronRight size={13} />
                                        <span className="flex-1">{t('finMoveKind', 'Move {kind}', { kind: kindLabel })}</span>
                                    </button>
                                    <div className="border-t border-pai-hairline my-1.5" />
                                    <button
                                        onClick={() => { setMenuOpen(false); onDelete(expense); }}
                                        className="w-full text-left px-3.5 py-2 text-sm text-pai-red hover:bg-[rgba(240,68,56,0.12)] transition-colors flex items-center gap-2.5"
                                    >
                                        <Trash2 size={13} />
                                        {t('commonDelete', 'Delete')}
                                    </button>
                                </div>
                            </>,
                            document.body,
                        )}
                        {moveOpen && (
                            <Modal
                                onClose={() => setMoveOpen(false)}
                                zIndex="z-[80]"
                                size="sm"
                                layout="frame"
                                title={t('finMoveKind', 'Move {kind}', { kind: kindLabel })}
                                cardTestId="ticket-move-dialog"
                                bodyTestId="ticket-move-list"
                                bodyClassName="py-1.5"
                            >
                                {moveTargets.length === 0 ? (
                                    <div className="px-5 py-3 text-[13px] text-pai-faint">
                                        {t('finNoOtherBooksYet', 'No other books yet.')}
                                    </div>
                                ) : moveTargets.map((mt) => (
                                    <button
                                        key={mt.key}
                                        data-testid="ticket-move-target"
                                        onClick={() => { setMoveOpen(false); onAssign(expense, mt.id); }}
                                        className="w-full text-left px-5 py-2.5 text-sm text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors truncate"
                                    >
                                        {mt.name}
                                    </button>
                                ))}
                            </Modal>
                        )}
                        {recurrenceTarget && (
                            <RecurrenceDialog
                                expense={recurrenceTarget}
                                fromOccurrence={recurrenceTarget.id !== expense.id}
                                onSave={(updates) => onUpdate?.(recurrenceTarget.id, updates)}
                                onClose={() => setRecurrenceTarget(null)}
                            />
                        )}
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Shared-book member management ──────────────────────────────────────
// ── Manual entry (in-book "+") — single-step: fill the form, save directly.
// The old redundant "Review/Summary" screen was removed (Manual entry ledger
// ticket): the user saves straight from the input form, and the footer is two
// high-contrast buttons — Cancel (red) and Save (green).
const ManualEntryDialog = ({ open, defaultBook, onClose, onSaved }) => {
    const { t } = useLanguage();
    // Default new entries to the user's base currency (Default Currency #124).
    const { currency: baseCurrency } = useCurrencyPreference();
    const todayIso = () => new Date().toISOString().slice(0, 10);
    const blankForm = () => ({
        direction: 'expense',
        amount: '',
        currency: baseCurrency || 'USD',
        merchant: '',
        category: '',
        description: '',
        expense_date: todayIso(),
        recurrence: '',   // #190: empty = one-time (default)
    });
    const [form, setForm] = useState(blankForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Reset the form when the dialog opens or the base currency changes. `blankForm`
    // is a helper recreated each render, so listing it would reset the form on every
    // render (wiping in-progress input); the open/baseCurrency keys are sufficient.
    useEffect(() => {
        if (open) {
            setForm(blankForm());
            setError(null);
        }
    }, [open, baseCurrency]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!open) return null;

    const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));
    const validate = () => {
        const amt = parseFloat(form.amount);
        if (!amt || amt <= 0) {
            setError(t('finAmountMustBeGreater', 'Amount must be greater than zero.'));
            return false;
        }
        if (!form.merchant.trim()) {
            setError(form.direction === 'income' ? t('finSourcePayerRequired', 'Source / payer is required.') : t('finMerchantRequired', 'Merchant is required.'));
            return false;
        }
        setError(null);
        return true;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        setError(null);
        try {
            const body = {
                direction: form.direction,
                amount: parseFloat(form.amount),
                currency: (form.currency || 'USD').toUpperCase(),
                merchant: form.merchant.trim(),
                category: form.category.trim() || null,
                description: form.description.trim() || null,
                expense_date: form.expense_date || todayIso(),
                book_id: defaultBook?.id || 'unassigned',
                origin: 'manual_ui',
                // #190: empty/unfilled → the BE treats it as a one-time entry.
                recurrence: form.recurrence || null,
            };
            const saved = await expenseService.createExpense(body);
            onSaved?.(saved);
            onClose?.();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal onClose={onClose} zIndex="z-[80]" layout="column" cardClassName="max-h-[90dvh]">
                <div className="flex items-center justify-between p-5 border-b border-pai-subtle">
                    <h3 className="text-[17px] font-bold text-white">
                        {t('finNewTransaction', 'New transaction')}
                    </h3>
                    <button onClick={onClose} className="pai-icon-btn w-[34px] h-[34px]">
                        <XIcon size={16} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                        <form
                            onSubmit={(e) => { e.preventDefault(); handleSave(); }}
                            className="space-y-4"
                        >
                            {/* Segmented control — the handoff's toggle idiom: a 4px
                                padded wrapper, each segment radius 8, the selected one
                                a white wash (never an accent fill; the accent stays on
                                the TEXT, which is the semantic-colour rule). */}
                            <div className="inline-flex gap-1 p-1 rounded-ctl border border-pai bg-white/[0.03] text-[13.5px] font-semibold">
                                {['expense', 'income'].map((d) => (
                                    <button
                                        type="button"
                                        key={d}
                                        onClick={() => update('direction', d)}
                                        className={`px-4 py-1.5 rounded-seg transition-colors ${form.direction === d
                                            ? `bg-white/[0.1] ${d === 'income' ? 'text-pai-green' : 'text-pai-red'}`
                                            : 'text-white/50 hover:text-white'}`}
                                    >
                                        {d === 'income' ? t('finIncomeWord', 'Income') : t('finExpenseWord', 'Expense')}
                                    </button>
                                ))}
                            </div>

                            <div className="grid grid-cols-[1fr_80px] gap-2">
                                <label className="block">
                                    <span className="text-[12.5px] font-medium text-pai-secondary">{t('commonAmount', 'Amount')}</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={form.amount}
                                        autoFocus
                                        onChange={(e) => update('amount', e.target.value)}
                                        className="pai-input tnum mt-1.5 w-full px-3 h-[42px] text-sm"
                                        required
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-[12.5px] font-medium text-pai-secondary">{t('commonCurrency', 'Currency')}</span>
                                    <input
                                        type="text"
                                        maxLength={3}
                                        data-testid="manual-entry-currency"
                                        value={form.currency}
                                        onChange={(e) => update('currency', e.target.value.toUpperCase())}
                                        className="pai-input mt-1.5 w-full px-3 h-[42px] text-sm uppercase"
                                    />
                                </label>
                            </div>

                            <label className="block">
                                <span className="text-[12.5px] font-medium text-pai-secondary">
                                    {form.direction === 'income' ? t('finSourcePayerLabel', 'Source / payer') : t('finMerchantPlaceholder', 'Merchant')}
                                </span>
                                <input
                                    type="text"
                                    value={form.merchant}
                                    onChange={(e) => update('merchant', e.target.value)}
                                    className="pai-input mt-1.5 w-full px-3 h-[42px] text-sm [color-scheme:dark]"
                                    required
                                />
                            </label>

                            <div className="grid grid-cols-2 gap-2">
                                <label className="block">
                                    <span className="text-[12.5px] font-medium text-pai-secondary">{t('commonCategory', 'Category')}</span>
                                    <input
                                        type="text"
                                        value={form.category}
                                        onChange={(e) => update('category', e.target.value)}
                                        placeholder={form.direction === 'income' ? t('finCategoryPlaceholderIncome', 'Salary, Refund…') : t('finCategoryPlaceholderExpense', 'Meals, Travel…')}
                                        className="pai-input mt-1.5 w-full px-3 h-[42px] text-sm [color-scheme:dark]"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-[12.5px] font-medium text-pai-secondary">{t('commonDate', 'Date')}</span>
                                    <input
                                        type="date"
                                        value={form.expense_date}
                                        onChange={(e) => update('expense_date', e.target.value)}
                                        className="pai-input mt-1.5 w-full px-3 h-[42px] text-sm [color-scheme:dark]"
                                    />
                                </label>
                            </div>

                            <label className="block">
                                <span className="text-[12.5px] font-medium text-pai-secondary">{t('finDescriptionOptional', 'Description (optional)')}</span>
                                <input
                                    type="text"
                                    value={form.description}
                                    onChange={(e) => update('description', e.target.value)}
                                    className="pai-input mt-1.5 w-full px-3 h-[42px] text-sm [color-scheme:dark]"
                                />
                            </label>

                            {/* #190: Recurring selector — empty = one-time (default). */}
                            <label className="block">
                                <span className="text-[12.5px] font-medium text-pai-secondary">{t('finRecurring', 'Recurring')}</span>
                                <select
                                    data-testid="manual-entry-recurring"
                                    value={form.recurrence}
                                    onChange={(e) => update('recurrence', e.target.value)}
                                    className="pai-input mt-1.5 w-full px-3 h-[42px] text-sm [color-scheme:dark]"
                                >
                                    <option value="">{t('finOneTimeDefault', 'One-time (default)')}</option>
                                    {RECURRENCE_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </label>
                            <p data-testid="manual-entry-recurring-note" className="text-[12.5px] text-pai-faint">
                                {t('finRecurrenceNote', '* If these options do not match your recurring time, ask PAI in the main chat or via Telegram to set your specific schedule (e.g., “Make this repeat every 27 days”).')}
                            </p>

                            {error && (
                                <div className="text-[13px] text-pai-red">{error}</div>
                            )}
                            <p className="text-[12.5px] text-pai-faint">
                                {t('finPressEnterPrefix', 'Press')} <kbd className="px-1.5 py-0.5 rounded-chip border border-pai-strong font-mono text-[11px] text-pai-secondary">{t('finEnterKey', 'Enter')}</kbd> {t('finPressEnterSuffix', 'to save.')}
                            </p>
                            <button type="submit" className="hidden" aria-hidden="true" />
                        </form>
                </div>

                {/* Footer: the design system has exactly ONE filled button — the white
                    primary. The old red Cancel / green Save pair predates it; red is
                    reserved for destructive meaning, and cancelling a form is not
                    destructive, so Cancel is the ghost and Save the primary. */}
                <div className="px-5 py-4 border-t border-pai-subtle flex justify-end gap-2.5">
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="pai-btn-ghost disabled:opacity-50"
                    >
                        {t('commonCancel', 'Cancel')}
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="pai-btn-primary disabled:opacity-50"
                    >
                        {saving ? t('finSavingEllipsis', 'Saving…') : t('commonSave', 'Save')}
                    </button>
                </div>
            </Modal>
    );
};




// Compact action button replacing the inline receipt thumbnail. Opens a
// menu with Download / Preview options so the heavy image never takes up
// space in the expense card or focus overlay.
const TicketImageButton = ({ documentId, filename = 'ticket', onPreview, className = '', size = 'sm' }) => {
    const { t } = useLanguage();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    if (!documentId) {
        return (
            <span data-testid="ticket-document-none" className={`inline-flex items-center gap-1.5 text-[11.5px] text-pai-faint italic ${className}`}>
                <ImageIcon size={size === 'lg' ? 13 : 11} />
                {t('finNoTicketImage', 'No ticket image or document.')}
            </span>
        );
    }

    const handleDownload = async (e) => {
        e.stopPropagation();
        setOpen(false);
        try { await documentService.downloadDocument(documentId, filename); } catch (_) { /* swallow */ }
    };

    const handlePreview = (e) => {
        e.stopPropagation();
        setOpen(false);
        onPreview?.();
    };

    const sizeCls = size === 'lg'
        ? 'text-[13px] px-3 py-1.5'
        : 'text-[11.5px] px-2.5 py-1';

    return (
        <div ref={wrapRef} className={`relative inline-block ${className}`} onClick={(e) => e.stopPropagation()}>
            <button
                type="button"
                data-testid="ticket-document-button"
                onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
                className={`inline-flex items-center gap-1.5 font-medium ${sizeCls} rounded-full border border-pai text-pai-secondary hover:text-white hover:border-white/30 transition-colors`}
                title={t('finTicketImageTitle', 'Ticket image')}
            >
                <ImageIcon size={size === 'lg' ? 14 : 12} />
                {t('finTicketImage', 'Ticket Image')}
            </button>
            {open && (
                <div className="absolute right-0 mt-1.5 w-36 bg-pai-modal border border-pai rounded-ctl shadow-modal-sm z-[70] py-1.5">
                    <button
                        type="button"
                        data-testid="ticket-document-preview"
                        onClick={handlePreview}
                        className="w-full text-left px-3.5 py-2 text-[13px] text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors"
                    >
                        {t('commonPreview', 'Preview')}
                    </button>
                    <button
                        type="button"
                        onClick={handleDownload}
                        className="w-full text-left px-3.5 py-2 text-[13px] text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors"
                    >
                        {t('commonDownload', 'Download')}
                    </button>
                </div>
            )}
        </div>
    );
};

// ── Digital Receipt Focus Overlay ─────────────────────────────────────────
const ExpenseFocusOverlay = ({ expense, books, viewerUserId, displayCurrency, onClose, onPhotoOpen, onAssign, onDelete }) => {
    const { t } = useLanguage();
    // Hooks must run unconditionally (rules-of-hooks). This overlay re-renders with a
    // null `expense` whenever it closes (focusExpense toggles null↔object), so the
    // hooks below were previously being skipped by the `if (!expense) return null`
    // early return, changing the hook count between renders (React crash). All hooks
    // are hoisted above the early return and derive their inputs null-safely.

    // Transparent FX breakdown (Exchange rate #67): when the receipt's original
    // currency differs from the ledger's display currency, fetch the conversion at
    // the TRANSACTION-date historical rate and show original / converted / the rate.
    const origCurrency = (expense?.currency || 'USD').toUpperCase();
    const showFx = !!expense && !!displayCurrency && displayCurrency.toUpperCase() !== origCurrency;
    const expenseId = expense?.id;

    const isOpen = !!expense;
    useEffect(() => {
        // Escape closes ONLY the ticket detail (#440, the #272 pattern): capture
        // phase on window + stopPropagation, so PanelShell's own Escape does not
        // also close the whole Finance panel behind it. When a receipt preview is
        // open on top, this yields to it (its own handler closes just that).
        if (!isOpen) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (document.querySelector('[data-testid="expense-lightbox"], [data-testid="document-preview-modal"]')) return;
            e.stopPropagation();
            onClose();
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [onClose, isOpen]);

    const [conversion, setConversion] = useState(null);
    useEffect(() => {
        if (!showFx || !expenseId) { setConversion(null); return; }
        let cancelled = false;
        expenseService.getConversion(expenseId, displayCurrency)
            .then((res) => { if (!cancelled) setConversion(res); })
            .catch(() => { if (!cancelled) setConversion(null); });
        return () => { cancelled = true; };
    }, [expenseId, displayCurrency, showFx]);

    if (!expense) return null;
    const items = expense.items || [];
    const unclassified = expense.unclassified_amount;
    const isIncome = expense.direction === 'income';

    // System entry time (created_at) — the "Uploaded" audit line shown at the
    // bottom of the colored header, distinct from the transaction date/time
    // (Upload Time ticket). Rendered DD/MM/YYYY - HH:MM in the viewer's LOCAL
    // timezone — expenses.created_at is a naive UTC timestamp, so formatUploadedAt
    // treats it as UTC and localizes it (Localization of Upload Timestamps #63).
    const uploadedLabel = formatUploadedAt(expense.created_at);

    const canModify = (() => {
        if (!expense.book_id) return expense.created_by_user_id === viewerUserId;
        const book = books.find((b) => b.id === expense.book_id);
        if (!book) return false;
        if (book.viewer_role === 'owner') return true;
        return expense.created_by_user_id === viewerUserId;
    })();

    return (
        // Escape is the window-capture handler above (it yields to a receipt
        // preview on top), so the Modal leaves it alone.
        <Modal
            onClose={onClose}
            zIndex="z-[80]"
            size="none"
            layout="column"
            closeOnEscape={false}
            className="sm:p-10"
            cardClassName="w-full max-w-3xl max-h-[90dvh] overflow-hidden animate-fade-slide-in"
            scrimTestId="expense-focus"
            scrimProps={{ 'data-expense-id': expense.id }}
        >
                {/* Header — the accent lives on the FIGURE, never as a filled band:
                    an accent may only tint text, a 1px border, an icon or a <=12%
                    wash, so the old emerald/rose header fill is gone. */}
                <div className="px-8 pt-7 pb-6 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="pai-eyebrow">
                            {isIncome ? t('finIncomeWord', 'Income') : t('finExpenseWord', 'Expense')} · {expense.book_name || t('finUnassigned', 'Unassigned')}
                        </div>
                        <div className={`mt-1.5 tnum text-[34px] font-extrabold tracking-[-0.03em] ${isIncome ? 'text-pai-green' : 'text-white'}`}>
                            {isIncome ? '+' : ''}
                            {formatAmount(expense.amount, expense.currency)}
                        </div>
                        <div className="mt-1 text-[13.5px] text-pai-secondary truncate">
                            {expense.merchant || t('finUnknownMerchant', 'Unknown merchant')}
                            {expense.expense_date && ` · ${formatDate(expense.expense_date)}`}
                            {expense.expense_time && ` · ${expense.expense_time}`}
                            {expense.category && ` · ${expense.category}`}
                        </div>
                        {/* System entry time (Upload Time ticket). expense-uploaded is
                            asserted with a STRICT toHaveText regex — do not add text. */}
                        {uploadedLabel && (
                            <div data-testid="expense-uploaded" className="mt-2 tnum text-[13px] text-pai-meta">
                                <span className="font-semibold">{t('finUploadedLabel', 'Uploaded:')}</span> {uploadedLabel}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <TicketImageButton
                            documentId={expense.document_id}
                            filename={expense.merchant ? `${expense.merchant}-${expense.expense_date || ''}` : 'ticket'}
                            onPreview={() => onPhotoOpen?.(expense)}
                            size="lg"
                        />
                        <button
                            onClick={onClose}
                            className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0"
                            title={t('finCloseEscTitle', 'Close (Esc)')}
                        >
                            <XIcon size={18} />
                        </button>
                    </div>
                </div>

                {/* Body: digital itemization only — the receipt photo is hidden
                    by spec and reachable via the Ticket Image button above.
                    #506: ONE element is both the flex item and the scroller. The
                    old `overflow-hidden` wrapper + inner `h-full overflow-y-auto`
                    never scrolled: the card only has a max-height, so `h-full`
                    did not resolve, the inner box grew to its content and the
                    wrapper clipped the rest of the item list. */}
                <div data-testid="expense-detail-body" className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-8 pb-2">
                        {/* Transparent FX breakdown (Exchange rate #67): original vs
                            converted amount + the exact historical rate on the ticket
                            date. Shown only when the receipt's currency differs from
                            the ledger's display currency and a rate was available. */}
                        {showFx && conversion?.converted && (
                            <div data-testid="expense-fx-breakdown" className="mb-3.5 rounded-bubble border border-pai-subtle bg-white/[0.025] px-6 py-5">
                                <div className="pai-eyebrow mb-3">{t('finCurrencyConversion', 'Currency conversion')}</div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <div className="text-[13.5px] text-pai-meta">{t('finOriginalAmount', 'Original amount')}</div>
                                        <div className="mt-1 tnum text-[21px] font-bold text-white">{formatAmount(conversion.original_amount, conversion.original_currency)}</div>
                                    </div>
                                    <div>
                                        <div className="text-[13.5px] text-pai-meta">{t('finConvertedCurrency', 'Converted ({currency})', { currency: conversion.target_currency })}</div>
                                        <div className="mt-1 tnum text-[21px] font-bold text-white">{formatAmount(conversion.converted_amount, conversion.target_currency)}</div>
                                    </div>
                                </div>
                                <div className="mt-3.5 tnum text-[13px] text-pai-meta">
                                    {t('finExchangeRateOn', 'Exchange rate on {date}: 1 {orig} = {rate} {target}', { date: formatDate(conversion.rate_date), orig: conversion.original_currency, rate: Number(conversion.rate).toFixed(4), target: conversion.target_currency })}
                                </div>
                                {/* #489: name the rate's source — the ECB fixing, or Frankfurter's
                                    blend of central-bank rates for currencies the ECB does not
                                    publish (RUB, AED, TWD), which is approximate. */}
                                {conversion.rate_source && (
                                    <div data-testid="expense-fx-source" data-source={conversion.rate_source} className="mt-1 text-[12.5px] text-pai-meta">
                                        {conversion.rate_source === 'frankfurter_blend'
                                            ? t('finRateSourceBlend', 'Source: Frankfurter, a blend of central-bank rates (approximate)')
                                            : t('finRateSourceEcb', 'Source: European Central Bank')}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="rounded-bubble border border-pai-subtle bg-white/[0.025] px-6 py-5">
                        <div className="flex items-center justify-between gap-3 mb-1">
                            <h4 className="pai-eyebrow">
                                {t('finDigitalReceipt', 'Digital receipt')}
                            </h4>
                            {expense.description && (
                                <span className="text-[13px] text-pai-meta italic truncate ml-2">
                                    {expense.description}
                                </span>
                            )}
                        </div>

                        {items.length === 0 ? (
                            <p className="mt-3 text-[14px] text-pai-meta">
                                {t('finNoItemization', 'No itemization captured from this receipt.')}
                            </p>
                        ) : (
                            <ul>
                                {items.map((it, idx) => (
                                    <li key={idx} data-testid="expense-item" className="py-3 flex items-baseline justify-between gap-4 border-t border-pai-hairline">
                                        <span className="text-[14px] font-medium tracking-[0.02em] text-pai-body">
                                            {it.quantity && it.quantity !== 1 && (
                                                <span className="tnum text-pai-meta mr-1">
                                                    {it.quantity}×
                                                </span>
                                            )}
                                            {it.name}
                                        </span>
                                        <span className="tnum text-[14px] font-semibold text-white flex-shrink-0">
                                            {it.price != null ? formatAmount(it.price, expense.currency) : '—'}
                                        </span>
                                    </li>
                                ))}
                                {unclassified != null && (
                                    <li className="py-3 flex items-baseline justify-between gap-4 border-t border-pai-hairline">
                                        <span className="text-[14px] italic text-pai-amber">
                                            {t('finUnclassifiedItems', 'Unclassified items')}
                                            <span className="ml-1.5 text-[11.5px] text-pai-amber/70">
                                                {t('finReconcilesTotal', '(reconciles total)')}
                                            </span>
                                        </span>
                                        <span className="tnum text-[14px] font-semibold text-pai-amber flex-shrink-0">
                                            {formatAmount(unclassified, expense.currency)}
                                        </span>
                                    </li>
                                )}
                                <li className="py-3 flex items-baseline justify-between gap-4 border-t border-pai-strong">
                                    <span className="text-[14px] font-bold text-white">{t('finTotalWord', 'Total')}</span>
                                    <span className="tnum text-[17px] font-extrabold text-white flex-shrink-0">
                                        {formatAmount(expense.amount, expense.currency)}
                                    </span>
                                </li>
                            </ul>
                        )}
                    </div>
                </div>

                {canModify && (
                    <div className="px-8 py-6 flex items-center justify-end gap-2.5">
                        <button
                            onClick={() => onAssign?.(expense, !expense.book_id ? null : expense.book_id, isIncome ? 'expense' : 'income')}
                            className="pai-btn-secondary"
                            title={t('finSwapDirectionTitle', 'Swap direction')}
                        >
                            {t('finMarkAsKind', 'Mark as {kind}', { kind: isIncome ? t('finExpenseWord', 'Expense') : t('finIncomeWord', 'Income') })}
                        </button>
                        <button
                            onClick={() => { onDelete?.(expense); }}
                            className="pai-btn-danger"
                        >
                            <Trash2 size={15} />
                            {t('commonDelete', 'Delete')}
                        </button>
                    </div>
                )}
            </Modal>
    );
};

// Full-screen receipt lightbox triggered from the TicketImage button's
// "Preview" action (in card and Focus Overlay). Handles BOTH kinds of linked
// receipt (#440): an image renders as-is; a PDF invoice renders its first
// page as an image (the authed /thumbnail endpoint, PyMuPDF) so it previews
// exactly like a photo — and offers the full multi-page document through the
// platform's standard DocumentPreviewModal. The type is read off the fetched
// blob, so no extra metadata request (and no serializer change) is needed.
const ExpensePhotoLightbox = ({ expense, onClose }) => {
    const { t } = useLanguage();
    const docId = expense?.document_id;
    const { url, contentType, error } = useDocumentBlobUrl(docId, !!docId, { path: 'download' });
    const isPdf = isPdfContentType(contentType);
    const { url: pageUrl, error: pageError } = useDocumentBlobUrl(docId, !!docId && isPdf, { path: 'thumbnail' });
    const [fullDocOpen, setFullDocOpen] = useState(false);
    useEffect(() => { setFullDocOpen(false); }, [docId]);
    useEffect(() => {
        // While the full document is open, Escape closes ONLY it (#272 pattern):
        // a CAPTURE-phase listener on window runs before the document-level
        // Escape handlers of the ticket detail and the preview modal, and
        // stopPropagation keeps them from also closing the ticket detail behind.
        if (!fullDocOpen) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            setFullDocOpen(false);
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [fullDocOpen]);
    useEffect(() => {
        // While the full-document modal is open, the listener above owns Escape.
        // Otherwise Escape closes ONLY the lightbox (capture + stopPropagation),
        // never the ticket detail or the Finance panel behind it.
        if (fullDocOpen || !docId) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            onClose();
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [onClose, fullDocOpen, docId]);
    if (!expense || !docId) return null;
    const alt = expense.merchant || t('finReceiptAlt', 'Receipt');
    let body;
    if (!url && !error) {
        body = <div className="text-pai-secondary text-sm">{t('finLoadingEllipsis', 'Loading…')}</div>;
    } else if (error) {
        body = <div className="text-pai-secondary text-sm">{t('finReceiptLoadFailed', 'Could not load this document.')}</div>;
    } else if (isPdf) {
        body = (
            <div
                className="flex flex-col items-center gap-3 max-w-full max-h-full min-w-0"
                onClick={(e) => e.stopPropagation()}
            >
                {pageUrl ? (
                    <img
                        data-testid="expense-lightbox-pdf-page"
                        src={pageUrl}
                        alt={alt}
                        className="max-w-full max-h-[78dvh] object-contain rounded-card shadow-modal bg-white"
                    />
                ) : pageError ? (
                    <div className="text-pai-secondary text-sm">{t('finPdfPageFailed', 'Could not render this PDF page.')}</div>
                ) : (
                    <div className="text-pai-secondary text-sm">{t('finLoadingEllipsis', 'Loading…')}</div>
                )}
                <button
                    type="button"
                    data-testid="expense-lightbox-open-document"
                    onClick={() => setFullDocOpen(true)}
                    className="pai-btn-secondary inline-flex items-center gap-1.5"
                >
                    <FileText size={14} />
                    {t('finOpenFullDocument', 'Open full document')}
                </button>
            </div>
        );
    } else {
        body = (
            <img
                data-testid="expense-lightbox-image"
                src={url}
                alt={alt}
                className="max-w-full max-h-full object-contain rounded-card shadow-modal"
                onClick={(e) => e.stopPropagation()}
            />
        );
    }
    // Portaled to document.body (#497): since #479 the ticket detail is a <Modal>,
    // which portals to <body>; left in the panel's tree this z-[90] overlay was
    // trapped in the panel's stacking context and painted BEHIND the z-[80] detail.
    return createPortal(
        <>
            {/* The lightbox steps aside while the full document is open; the
                preview is raised above the z-[80] ticket detail behind it. */}
            {!fullDocOpen && <div
                data-testid="expense-lightbox"
                data-kind={!url ? 'loading' : (isPdf ? 'pdf' : 'image')}
                className="fixed inset-0 z-[90] bg-black/[0.85] backdrop-blur-scrim flex items-center justify-center p-4 cursor-zoom-out"
                onClick={onClose}
            >
                <button
                    onClick={onClose}
                    className="pai-icon-btn absolute top-5 right-5 w-[38px] h-[38px] rounded-full"
                    title={t('finCloseEscTitle', 'Close (Esc)')}
                >
                    <XIcon size={20} />
                </button>
                {body}
            </div>}
            {fullDocOpen && (
                <DocumentPreviewModal
                    documentId={docId}
                    documentName={alt}
                    zClass="z-[95]"
                    onClose={() => setFullDocOpen(false)}
                />
            )}
        </>,
        document.body,
    );
};

const TabButton = ({ active, onClick, label, count, icon, highlight, isDropTarget, isDraggingOver, onDragOver, onDragEnter, onDragLeave, onDrop }) => (
    <button
        onClick={onClick}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`flex items-center gap-2 pt-[11px] pb-[13px] px-5 text-[14px] whitespace-nowrap border-b-[3px] transition-colors ${
            active
                ? 'border-white text-white font-bold'
                : 'border-transparent text-white/50 font-medium hover:text-white'
        } ${
            isDraggingOver
                ? 'bg-white/[0.09] ring-1 ring-white/40 ring-inset'
                : isDropTarget
                ? 'bg-white/[0.03]'
                : ''
        }`}
    >
        {icon}
        <span>{label}</span>
        {count > 0 && (
            <span className={`tnum text-[11.5px] font-semibold px-2 py-0.5 rounded-full border ${
                highlight
                    ? 'border-[rgba(245,165,36,0.5)] text-pai-amber'
                    : active
                    ? 'border-pai-strong text-white'
                    : 'border-pai-subtle text-pai-meta'
            }`}>
                {count}
            </span>
        )}
    </button>
);

const NewBookDialog = ({ open, onClose, onCreate }) => {
    const { t } = useLanguage();
    const [name, setName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) { setName(''); setError(null); setSubmitting(false); }
    }, [open]);

    if (!open) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed) return;
        setSubmitting(true);
        setError(null);
        try {
            await onCreate(trimmed);
            onClose();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal onClose={onClose} zIndex="z-[60]" cardClassName="p-7">
                <h3 className="text-[19px] font-bold text-white mb-1.5">{t('finNewBookTitle', 'New book')}</h3>
                <p className="text-[14px] leading-[1.6] text-pai-meta mb-5">
                    {t('finNewBookDesc', 'A book is a ledger for grouping transactions — e.g. "PAI Operations", "Personal", "Travel".')}
                </p>
                <form onSubmit={handleSubmit}>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('finBookNamePlaceholder', 'Book name')}
                        autoFocus
                        className="pai-input w-full px-3.5 h-[44px] text-[15px]"
                    />
                    {error && <div className="mt-2.5 text-[13.5px] text-pai-red">{error}</div>}
                    <div className="mt-4 flex justify-end gap-2">
                        <button type="button" onClick={onClose} className="pai-btn-ghost">
                            {t('commonCancel', 'Cancel')}
                        </button>
                        <button type="submit" disabled={!name.trim() || submitting} className="pai-btn-primary disabled:opacity-40">
                            {submitting ? t('finCreatingEllipsis', 'Creating…') : t('finCreate', 'Create')}
                        </button>
                    </div>
                </form>
            </Modal>
    );
};

// Rename the current book (Options → Rename book) — Ledger UI Reorganization.
const RenameBookDialog = ({ open, currentName, onClose, onRename }) => {
    const { t } = useLanguage();
    const [name, setName] = useState(currentName || '');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) { setName(currentName || ''); setError(null); setSubmitting(false); }
    }, [open, currentName]);

    if (!open) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed) return;
        setSubmitting(true);
        setError(null);
        try {
            await onRename(trimmed);
        } catch (err) {
            setError(userErrorMessage(err, t));
            setSubmitting(false);
        }
    };

    return (
        <Modal onClose={onClose} zIndex="z-[80]" cardClassName="p-7">
                <h3 className="text-[19px] font-bold text-white mb-4">{t('finRenameBookTitle', 'Rename book')}</h3>
                <form onSubmit={handleSubmit}>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('finBookNamePlaceholder', 'Book name')}
                        autoFocus
                        className="pai-input w-full px-3.5 h-[44px] text-[15px]"
                    />
                    {error && <div className="mt-2.5 text-[13.5px] text-pai-red">{error}</div>}
                    <div className="mt-4 flex justify-end gap-2">
                        <button type="button" onClick={onClose} className="pai-btn-ghost">
                            {t('commonCancel', 'Cancel')}
                        </button>
                        <button type="submit" disabled={!name.trim() || submitting} className="pai-btn-primary disabled:opacity-40">
                            {submitting ? t('finSavingEllipsis', 'Saving…') : t('commonSave', 'Save')}
                        </button>
                    </div>
                </form>
            </Modal>
    );
};

// Destructive confirmation for Options → Eliminate book — deletes the book AND
// all its transactions (Ledger UI Reorganization ticket). #498 follow-up 2: when
// the book's transactions have linked files, it offers "Also delete the N linked
// files" (unticked = keep them); the count comes from the server.
const EliminateBookDialog = ({ open, bookId, bookName, onClose, onConfirm }) => {
    const { t } = useLanguage();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [fileCount, setFileCount] = useState(0);
    const [deleteFiles, setDeleteFiles] = useState(false);

    useEffect(() => {
        if (open) { setError(null); setSubmitting(false); setDeleteFiles(false); setFileCount(0); }
    }, [open]);

    // No count (old server, network error) → no checkbox → the files are kept.
    useEffect(() => {
        if (!open || !bookId) return undefined;
        let cancelled = false;
        expenseService.bookLinkedFiles(bookId)
            .then((r) => { if (!cancelled) setFileCount(Number(r?.count) || 0); })
            .catch(() => { if (!cancelled) setFileCount(0); });
        return () => { cancelled = true; };
    }, [open, bookId]);

    if (!open) return null;

    const handleConfirm = async () => {
        setSubmitting(true);
        setError(null);
        try {
            await onConfirm({ deleteFiles: fileCount > 0 && deleteFiles });
        } catch (err) {
            setError(userErrorMessage(err, t));
            setSubmitting(false);
        }
    };

    return (
        <Modal onClose={onClose} zIndex="z-[80]" cardClassName="p-7">
                <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={20} className="text-pai-red flex-shrink-0" />
                    <h3 className="text-[19px] font-bold text-white">{t('finEliminateBookTitle', 'Eliminate “{bookName}”?', { bookName })}</h3>
                </div>
                <p className="text-[15px] leading-[1.6] text-pai-body">
                    {t('finEliminateBookConfirm', 'Are you sure you want to eliminate this book and all its transactions? This cannot be undone.')}
                </p>
                {fileCount > 0 && (
                    <div className="mt-4">
                        <LinkedFilesChoice count={fileCount} checked={deleteFiles} onChange={setDeleteFiles} disabled={submitting} />
                    </div>
                )}
                {error && <div className="mt-2.5 text-[13.5px] text-pai-red">{error}</div>}
                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" onClick={onClose} disabled={submitting} className="pai-btn-ghost disabled:opacity-50">
                        {t('commonCancel', 'Cancel')}
                    </button>
                    <button type="button" onClick={handleConfirm} disabled={submitting} className="pai-btn-danger disabled:opacity-50">
                        {submitting ? t('finEliminatingEllipsis', 'Eliminating…') : t('finEliminate', 'Eliminate')}
                    </button>
                </div>
            </Modal>
    );
};

const ExportDialog = ({ open, onClose, books, defaultBookKey, defaultCurrency = 'USD', supportedCurrencies = [] }) => {
    const { t } = useLanguage();
    const [bookKey, setBookKey] = useState(defaultBookKey || 'all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currency, setCurrency] = useState(defaultCurrency);
    const [includeItems, setIncludeItems] = useState(true);
    // Ticket Images toggle (Images Yes/No ticket): unchecking it ships a much
    // smaller, data-only ZIP (no receipts/ folder). Defaults to included.
    const [includeImages, setIncludeImages] = useState(true);
    // "Full Book" is a CONFIGURATION checkbox (Unified Export Workflow #62), not a
    // button: ticking it means "ignore the date range" — the export still only
    // fires on the final [Download ZIP] button, applying all settings at once.
    const [fullBook, setFullBook] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) { setError(null); setSubmitting(false); return; }
        setBookKey(defaultBookKey || 'all');
        setCurrency(defaultCurrency || 'USD');
        setIncludeItems(true);
        setIncludeImages(true);
        setFullBook(false);
    }, [open, defaultBookKey, defaultCurrency]);

    if (!open) return null;

    // Run the export with explicit dates (defaults to the form's current dates).
    // "Full Book" calls this with empty dates to export the entire ledger (Full
    // Book ticket) — an explicit shortcut for the old "leave dates blank" behavior.
    const runExport = async (start, end) => {
        setSubmitting(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (bookKey && bookKey !== 'all') params.set('book_id', bookKey);
            if (start) params.set('start_date', start);
            if (end) params.set('end_date', end);
            if (currency) params.set('display_currency', currency);
            if (includeItems) params.set('include_items', 'true');
            // Only send when OFF — absent param keeps the legacy "include images" default.
            if (!includeImages) params.set('include_images', 'false');

            const token = getAuthToken();
            const base = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
            const res = await fetch(`${base}/api/expenses/export?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                const j = await res.json().catch(() => null);
                throw httpError(res.status, j);
            }
            const blob = await res.blob();
            const cd = res.headers.get('Content-Disposition') || '';
            const m = /filename="?([^";]+)"?/.exec(cd);
            const fallbackName = `expenses_${new Date().toISOString().slice(0, 10)}.zip`;
            const filename = (m && m[1]) || fallbackName;
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);
            onClose();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setSubmitting(false);
        }
    };

    // The single export entry point — "Full Book" just means ignore the dates.
    const handleExport = (e) => {
        e.preventDefault();
        if (fullBook) runExport('', '');
        else runExport(startDate, endDate);
    };

    return (
        // #479 Scroll Lock: the card is bounded to the dynamic viewport and its
        // BODY scrolls while the Cancel / Download footer stays on screen — on a
        // phone this form is taller than the window, and the unbounded card used
        // to overflow both edges of the centred scrim with nothing scrollable.
        <Modal onClose={onClose} zIndex="z-[60]" layout="column" cardClassName="p-5 sm:p-7" cardTestId="export-dialog">
                <form onSubmit={handleExport} className="flex flex-col min-h-0 flex-1">
                <div data-testid="export-dialog-body" className="pai-dialog-body -mr-2 pr-2">
                <h3 className="text-[19px] font-bold text-white mb-1.5">{t('finAdvancedExportTitle', 'Advanced financial export')}</h3>
                <p className="text-[14px] leading-[1.6] text-pai-meta mb-5">
                    {t('finExportDescPart1', 'Downloads a ZIP with')} <span className="font-mono text-[12.5px] text-pai-secondary">expenses.xlsx</span> {t('finExportDescPart2', '(Summary & Ledger sheet, optional Itemized Breakdown + auto-aggregated Items Summary) and a')} <span className="font-mono text-[12.5px] text-pai-secondary">receipts/</span> {t('finExportDescPart3', 'folder containing each receipt image. Excel Tables enabled, columns auto-fit, positives green and negatives red.')}
                </p>
                <div className="space-y-3">
                    <label className="block">
                        <span className="text-[12.5px] font-medium text-pai-secondary">{t('finBookWord', 'Book')}</span>
                        <select value={bookKey} onChange={(e) => setBookKey(e.target.value)} className="pai-input mt-1.5 w-full px-3.5 h-[44px] text-[14px] [color-scheme:dark]">
                            <option value="all">{t('finAllBooks', 'All books')}</option>
                            <option value={UNASSIGNED_KEY}>{t('finUnassignedOnly', 'Unassigned only')}</option>
                            {books.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                            <span className="text-[12.5px] font-medium text-pai-secondary">{t('finStartDate', 'Start date')}</span>
                            <input type="date" value={startDate} disabled={fullBook} onChange={(e) => setStartDate(e.target.value)} className="pai-input mt-1.5 w-full px-3.5 h-[44px] text-[14px] [color-scheme:dark] disabled:opacity-40 disabled:cursor-not-allowed" />
                        </label>
                        <label className="block">
                            <span className="text-[12.5px] font-medium text-pai-secondary">{t('finEndDate', 'End date')}</span>
                            <input type="date" value={endDate} disabled={fullBook} onChange={(e) => setEndDate(e.target.value)} className="pai-input mt-1.5 w-full px-3.5 h-[44px] text-[14px] [color-scheme:dark] disabled:opacity-40 disabled:cursor-not-allowed" />
                        </label>
                    </div>
                    {/* "Full Book" is a config checkbox next to the dates (Unified Export
                        Workflow #62): ticking it ignores the date range, but the export
                        only runs when the final [Download ZIP] button is clicked. */}
                    <label className="flex items-center gap-2.5 text-[14px] text-pai-secondary cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={fullBook}
                            onChange={(e) => setFullBook(e.target.checked)}
                            className="w-4 h-4 accent-white rounded [color-scheme:dark]"
                            data-testid="export-full-book"
                        />
                        {t('finFullBookLabel', 'Full Book — export everything (ignore the date range)')}
                    </label>
                    <label className="block">
                        <span className="text-[12.5px] font-medium text-pai-secondary">{t('finDisplayCurrency', 'Display currency')}</span>
                        <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="pai-input mt-1.5 w-full px-3.5 h-[44px] text-[14px] [color-scheme:dark]">
                            {(supportedCurrencies.length > 0 ? supportedCurrencies : ['USD', 'EUR', 'GBP', 'JPY', 'THB']).map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                        <span className="mt-1.5 block text-[12.5px] text-pai-faint">
                            {t('finExportCurrencyNote', 'All totals + the Master Ledger amount column are normalized to {currency}. Original amounts are preserved on each row.', { currency })}
                        </span>
                    </label>
                    <label className="flex items-center gap-2.5 text-[14px] text-pai-secondary cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={includeItems}
                            onChange={(e) => setIncludeItems(e.target.checked)}
                            className="w-4 h-4 accent-white rounded [color-scheme:dark]"
                        />
                        {t('finIncludeItemizedBreakdown', 'Include Itemized Breakdown')}
                        <span className="text-[12.5px] text-pai-faint">
                            {t('finItemizedBreakdownNote', '(adds Sheet 2 with every product + Sheet 3 pivot grouped by item name)')}
                        </span>
                    </label>
                    <label className="flex items-center gap-2.5 text-[14px] text-pai-secondary cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={includeImages}
                            onChange={(e) => setIncludeImages(e.target.checked)}
                            className="w-4 h-4 accent-white rounded [color-scheme:dark]"
                        />
                        {t('finTicketImages', 'Ticket Images')}
                        <span className="text-[12.5px] text-pai-faint">
                            {t('finTicketImagesNote', '(bundles the receipts/ folder — untick for a much smaller, data-only ZIP)')}
                        </span>
                    </label>
                    <p className="text-[12.5px] text-pai-faint">{t('finLeaveDatesEmpty', 'Leave dates empty to include everything.')}</p>
                    {error && <div className="text-[13.5px] text-pai-red">{error}</div>}
                </div>
                </div>
                <div data-testid="export-dialog-footer" className="flex-shrink-0 flex justify-end gap-2 pt-4">
                    <button type="button" onClick={onClose} className="pai-btn-ghost">{t('commonCancel', 'Cancel')}</button>
                    <button type="submit" data-testid="export-dialog-submit" disabled={submitting} className="pai-btn-primary disabled:opacity-50">
                        <Download size={14} />
                        {submitting ? t('finBuildingEllipsis', 'Building…') : t('finDownloadZip', 'Download ZIP')}
                    </button>
                </div>
                </form>
            </Modal>
    );
};

// Post-Transfer Summary: a collapsible card that appears after a batch move,
// listing every moved transaction (merchant + amount) with Preview/Download, so
// the user can verify what moved without leaving the view. Scrollable for large
// batches (150+).
const TransferSummaryCard = ({ summary, onClose, onPreview }) => {
    const { t } = useLanguage();
    const [collapsed, setCollapsed] = useState(false);
    if (!summary) return null;
    const items = summary.items || [];
    const fmt = (a, c = 'USD') => {
        if (a == null) return '—';
        try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: c }).format(a); }
        catch (_) { return `${c} ${Number(a).toFixed(2)}`; }
    };
    return (
        <div className="fixed bottom-5 right-5 z-[70] w-80 max-w-[calc(100vw-2rem)] bg-pai-modal border border-pai rounded-card shadow-modal-sm" data-testid="transfer-summary">
            <div className="flex items-center justify-between px-4 py-3 border-b border-pai-subtle">
                <button
                    onClick={() => setCollapsed((c) => !c)}
                    className="flex items-center gap-2 text-[14px] font-semibold text-white min-w-0"
                >
                    <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
                    <span className="truncate">{t('finMovedToBook', 'Moved {count} to {book}', { count: items.length, book: summary.targetBookName })}</span>
                </button>
                <button onClick={onClose} title={t('commonClose', 'Close')} className="pai-icon-btn w-7 h-7 flex-shrink-0">
                    <XIcon size={14} />
                </button>
            </div>
            {!collapsed && (
                <div className="max-h-72 overflow-y-auto divide-y divide-pai-hairline">
                    {items.map((it) => (
                        <div key={it.id} className="flex items-center justify-between gap-2 px-3 py-2">
                            <div className="min-w-0">
                                <div className="text-[14px] font-medium text-white truncate">{it.merchant || '—'}</div>
                                <div className="tnum text-[12.5px] text-pai-meta">{fmt(it.amount, it.currency)}</div>
                            </div>
                            <TicketImageButton documentId={it.document_id} filename={it.merchant || 'ticket'} onPreview={() => onPreview?.(it)} size="sm" />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const ExpensesPanel = ({ onClose, mode, onToggleExpand }) => {
    const { t } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const focusExpenseId = urlParams.get('focus');
    const focusBookKey = urlParams.get('book'); // book id, or 'unassigned'
    const overlayParam = urlParams.get('overlay'); // '1' → open Focus Overlay on arrival

    const [books, setBooks] = useState([]);
    const [unassignedStats, setUnassignedStats] = useState({ expense_count: 0, total_amount: 0 });
    // Initial book: explicit URL `?book=...` wins, then the last visited
    // book stored in localStorage, then Unassigned as the safety default.
    const [selectedKey, setSelectedKey] = useState(focusBookKey || readLastBookKey() || UNASSIGNED_KEY);

    // Persist the active book on every change so the next session reopens here.
    useEffect(() => {
        writeLastBookKey(selectedKey);
    }, [selectedKey]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // A failed Eliminate (#428). Its own slot, not `error`: restoring the book
    // re-selects it, which re-runs fetchExpenses, which begins with setError(null)
    // and would wipe the message the user needs to see.
    const [bookDeleteError, setBookDeleteError] = useState(null);
    const [showNewBook, setShowNewBook] = useState(false);
    const [showExport, setShowExport] = useState(false);
    // Top-right overflow menu (Financial Cleanup #77): New Book / Export / Unassigned.
    const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
    // Anchored position for the portaled header menu (see the portal below).
    const headerMenuBtnRef = useRef(null);
    const [headerMenuPos, setHeaderMenuPos] = useState(null);
    const headerMenuRef = useRef(null);
    // #500 re-open: outside press closes (no scroll-locking backdrop); the effect
    // below re-anchors it on scroll/resize.
    useMenuDismiss(headerMenuOpen, () => setHeaderMenuOpen(false), [headerMenuBtnRef, headerMenuRef]);
    useEffect(() => {
        if (!headerMenuOpen) { setHeaderMenuPos(null); return; }
        const place = () => {
            const r = headerMenuBtnRef.current?.getBoundingClientRect();
            if (!r) return;
            setHeaderMenuPos({
                top: Math.round(r.bottom + 6),
                right: Math.max(8, Math.round(window.innerWidth - r.right)),
            });
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [headerMenuOpen]);
    const [showMembers, setShowMembers] = useState(false);
    const [showManualEntry, setShowManualEntry] = useState(false);
    // Consolidated book-level "Options" dropdown + its new manage actions
    // (Ledger UI Reorganization ticket). Options layout finance (#249): the menu
    // is PORTALED to document.body and anchored in viewport coords, so the panel's
    // own overflow/stacking context can never clip it — see optionsPos/openOptions.
    const [optionsOpen, setOptionsOpen] = useState(false);
    const [optionsPos, setOptionsPos] = useState(null);
    const optionsBtnRef = useRef(null);
    const optionsMenuRef = useRef(null);
    const [showRename, setShowRename] = useState(false);
    const [showEliminate, setShowEliminate] = useState(false);
    const [draggingExpense, setDraggingExpense] = useState(null);
    const [dragOverTab, setDragOverTab] = useState(null);
    const [selectionMode, setSelectionMode] = useState(false);
    // Finance Visualization Mode (#442): 'list' = the ledger, 'charts' = the
    // graphical summary of the SAME book. Session state on purpose (not stored):
    // the ledger is the module's home view, so a fresh open always lands there.
    const [viewMode, setViewMode] = useState('list');
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [focusExpense, setFocusExpense] = useState(null);  // open Focus Overlay
    const [photoExpense, setPhotoExpense] = useState(null);  // open lightbox
    const [transferSummary, setTransferSummary] = useState(null);  // post-batch-move summary
    const [deletingTemplate, setDeletingTemplate] = useState(null);  // #234 recurring-template delete
    const [deletingImpact, setDeletingImpact] = useState(null);      // #234 re-open: lineage count + source
    const [templateDeleteFiles, setTemplateDeleteFiles] = useState(false); // #498 follow-up
    // #498 follow-up (PO): a delete whose rows carry a linked file asks whether to
    // delete the file too (default KEEP) in a dialog; a delete with no linked file
    // keeps the plain browser confirm. `askDelete` resolves {deleteFiles} or null.
    const [deleteAsk, setDeleteAsk] = useState(null);           // { message, resolve }
    const [deleteAskFiles, setDeleteAskFiles] = useState(false);
    const askDelete = useCallback((message, rows) => {
        if (!hasAttachedFile(rows)) {
            return Promise.resolve(window.confirm(message) ? { deleteFiles: false } : null);
        }
        return new Promise((resolve) => {
            setDeleteAskFiles(false);
            setDeleteAsk({ message, resolve });
        });
    }, []);
    const settleDeleteAsk = (result) => {
        const ask = deleteAsk;
        setDeleteAsk(null);
        ask?.resolve(result);
    };
    const { user: authUser } = useAuth();

    // #234 re-open: fetch the delete IMPACT (whole-lineage count + preserved
    // source) when the recurring-delete dialog opens, so it shows the count of
    // entries Eliminate will wipe + the original ticket being kept. Falls back
    // gracefully (null) if it errors.
    useEffect(() => {
        if (!deletingTemplate) { setDeletingImpact(null); return undefined; }
        setTemplateDeleteFiles(false);
        let cancelled = false;
        expenseService.recurrenceImpact(deletingTemplate.id)
            .then((r) => { if (!cancelled) setDeletingImpact(r); })
            .catch(() => { if (!cancelled) setDeletingImpact(null); });
        return () => { cancelled = true; };
    }, [deletingTemplate]);

    // Exit selection mode whenever the user switches tabs.
    useEffect(() => {
        setSelectionMode(false);
        setSelectedIds(new Set());
    }, [selectedKey]);

    const toggleSelected = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const exitSelectionMode = useCallback(() => {
        setSelectionMode(false);
        setSelectedIds(new Set());
    }, []);

    // When the focus URL param changes, jump to that book.
    useEffect(() => {
        if (focusBookKey) setSelectedKey(focusBookKey);
    }, [focusBookKey]);

    // Once the matching expense has been scrolled to, drop the focus param
    // from the URL so future tab clicks don't keep re-focusing it.
    useEffect(() => {
        if (!focusExpenseId) return;
        const t = setTimeout(() => {
            const next = new URLSearchParams(location.search);
            next.delete('focus');
            next.delete('book');
            next.delete('overlay');
            const qs = next.toString();
            navigate(qs ? `${location.pathname}?${qs}` : location.pathname, { replace: true });
        }, 1500);
        return () => clearTimeout(t);
    }, [focusExpenseId, location.pathname, location.search, navigate]);

    // Deep link: when arriving with ?overlay=1&focus=<id>, open the Focus
    // Overlay as soon as the expense is loaded.
    useEffect(() => {
        if (overlayParam !== '1' || !focusExpenseId || focusExpense) return;
        const match = expenses.find((e) => e.id === focusExpenseId);
        if (match) setFocusExpense(match);
    }, [overlayParam, focusExpenseId, expenses, focusExpense]);

    // Declared here (rather than further down beside the summary fetch) because
    // fetchBooks below needs it: book totals are now converted server-side, and a
    // book with no base_currency of its own follows this preference (#250).
    const {
        currency: displayCurrency,
        setCurrency: setDisplayCurrency,
        supported: supportedCurrencies,
    } = useCurrencyPreference();

    // ── Last-ISSUED-wins guard (Finance Ui Lag #294) ────────────────────────────
    // Both loaders used to be last-ARRIVED-wins, which silently reverted a change
    // the user had just made. A ledger GET is answered from rows queried BEFORE a
    // just-committed PATCH, yet its RESPONSE can reach the browser AFTER the
    // post-save reload — /api/expenses converts every row's currency, which blocks
    // for seconds on an upstream rate miss (real 4s Frankfurter read timeouts show
    // up in this stack's own logs) — so the older request wrote its pre-change
    // snapshot over the newer, correct one and the panel stayed wrong until it was
    // closed and reopened. Activation is the path that loses this race: it is a
    // single click straight from the dialog (no intervening round-trip to let an
    // in-flight GET land first) and its own reload is the slow one, because
    // GET /api/expenses materializes the whole retroactive backfill under an
    // advisory lock. Deactivation opens the stop-cleanup bubble first, which fetches
    // the recurrence impact — that pause is why removal looked instant while
    // activation looked broken. A response from a superseded request is now dropped.
    const booksReqRef = useRef(0);
    const expensesReqRef = useRef(0);

    const fetchBooks = useCallback(async () => {
        const seq = ++booksReqRef.current;
        try {
            const res = await expenseService.listBooks({ displayCurrency });
            if (seq !== booksReqRef.current) return;   // superseded — drop it
            setBooks(res.books || []);
            setUnassignedStats(res.unassigned || { expense_count: 0, total_amount: 0 });
        } catch (err) {
            if (seq !== booksReqRef.current) return;
            setError(userErrorMessage(err, t));
        }
    }, [displayCurrency, t]);

    const fetchExpenses = useCallback(async (key) => {
        const seq = ++expensesReqRef.current;
        setLoading(true);
        setError(null);
        try {
            const res = await expenseService.listExpenses({
                bookId: key === UNASSIGNED_KEY ? 'unassigned' : key,
            });
            if (seq !== expensesReqRef.current) return;   // superseded — drop it
            setExpenses(res.expenses || []);
        } catch (err) {
            if (seq !== expensesReqRef.current) return;
            setError(userErrorMessage(err, t));
        } finally {
            // Only the newest request owns the spinner; a late loser must not
            // clear it while its successor is still in flight.
            if (seq === expensesReqRef.current) setLoading(false);
        }
    }, [t]);

    // Drop a deleted book from local state RIGHT NOW (#428). Bumping booksReqRef
    // is load-bearing: a books GET issued BEFORE the delete would otherwise land
    // afterwards and re-add the book it still knew about (last-issued-wins, #294).
    const selectedKeyRef = useRef(selectedKey);
    selectedKeyRef.current = selectedKey;
    const removeBookLocally = useCallback((bookId) => {
        if (!bookId) return;
        booksReqRef.current += 1;
        setBooks((prev) => (prev.some((b) => b.id === bookId) ? prev.filter((b) => b.id !== bookId) : prev));
        if (selectedKeyRef.current === bookId) {
            setExpenses([]);   // the deleted book's rows must not linger under Unassigned
            setSelectedKey(UNASSIGNED_KEY);
        }
    }, []);

    useEffect(() => { fetchBooks(); }, [fetchBooks]);
    useEffect(() => { fetchExpenses(selectedKey); }, [selectedKey, fetchExpenses]);

    // If the persisted last-book id was deleted (or the user lost access),
    // fall back to Unassigned the first time we get a books list back.
    useEffect(() => {
        if (selectedKey === UNASSIGNED_KEY) return;
        if (books.length === 0) return;
        const stillExists = books.some((b) => b.id === selectedKey);
        if (!stillExists) setSelectedKey(UNASSIGNED_KEY);
    }, [books, selectedKey]);

    const handleBulkDelete = useCallback(async () => {
        const ids = Array.from(selectedIds);
        if (!ids.length) return;
        const choice = await askDelete(
            t('finConfirmDeleteTransactions', 'Delete {n} transaction{s}? This cannot be undone.', { n: ids.length, s: ids.length === 1 ? '' : 's' }),
            expenses.filter((e) => selectedIds.has(e.id)),
        );
        if (!choice) return;
        setBulkBusy(true);
        try {
            const res = await expenseService.bulk({ ids, operation: 'delete', deleteFiles: choice.deleteFiles });
            await fetchBooks();
            await fetchExpenses(selectedKey);
            if (res.skipped?.length) {
                setError(t('finDeletedSkipped', 'Deleted {applied}; skipped {skipped} (permissions or missing).', { applied: res.applied.length, skipped: res.skipped.length }));
            }
            exitSelectionMode();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setBulkBusy(false);
        }
    }, [selectedIds, expenses, askDelete, fetchBooks, fetchExpenses, selectedKey, exitSelectionMode]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleBulkMove = useCallback(async (targetBookId) => {
        const ids = Array.from(selectedIds);
        if (!ids.length) return;
        // Capture the moved items' details now (before the list is refetched).
        const movedItems = expenses.filter((e) => selectedIds.has(e.id));
        const targetName = targetBookId
            ? (books.find((b) => b.id === targetBookId)?.name || t('finBookLowercase', 'book'))
            : t('finUnassigned', 'Unassigned');
        setBulkBusy(true);
        try {
            const res = await expenseService.bulk({
                ids,
                operation: 'move',
                bookId: targetBookId || 'unassigned',
            });
            await fetchBooks();
            await fetchExpenses(selectedKey);
            if (res.skipped?.length) {
                setError(t('finMovedSkipped', 'Moved {applied}; skipped {skipped} (permissions or missing).', { applied: res.applied.length, skipped: res.skipped.length }));
            }
            const appliedSet = new Set(res.applied || ids);
            setTransferSummary({
                targetBookName: targetName,
                items: movedItems.filter((e) => appliedSet.has(e.id)),
            });
            exitSelectionMode();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setBulkBusy(false);
        }
    }, [selectedIds, expenses, books, fetchBooks, fetchExpenses, selectedKey, exitSelectionMode, t]);

    // ── Chat-driven mutations apply INSTANTLY (Finance Lag #438) ─────────────────
    // A transaction PAI deletes or moves — through the chat card, a tool call, or a
    // Telegram turn — used to reach this panel only as "refetch", and the
    // refetch is the slow request in the module; the chat DELETE CARD fired an event
    // this panel did not listen to at all, so its rows never left until a reopen.
    // The event now says which row changed, so we apply it to local state at once
    // (pure reducers in utils/financeLiveState) and the debounced refetch below only
    // RECONCILES. Whenever state actually changed, the request counters are bumped
    // exactly like removeBookLocally (#428/#294): a GET issued BEFORE the mutation
    // must not land afterwards and put the row back.
    const liveStateRef = useRef({ expenses, books, unassigned: unassignedStats });
    liveStateRef.current = { expenses, books, unassigned: unassignedStats };
    const appliedDeletesRef = useRef(new Set());

    const applyLiveState = useCallback((reducer, detail) => {
        const cur = liveStateRef.current;
        const next = reducer(cur, detail, selectedKeyRef.current);
        if (next === cur) return false;
        liveStateRef.current = next;       // a second event in the same tick builds on this one
        booksReqRef.current += 1;
        expensesReqRef.current += 1;
        if (next.expenses !== cur.expenses) setExpenses(next.expenses);
        if (next.books !== cur.books) setBooks(next.books);
        if (next.unassigned !== cur.unassigned) setUnassignedStats(next.unassigned);
        return true;
    }, []);

    useEffect(() => {
        // documentService dispatches one omnis:expense-* per row; a multi-receipt
        // photo fires a burst. Trailing-debounce so N events → one refetch.
        const debounced = debounce(() => {
            fetchBooks();
            fetchExpenses(selectedKey);
        }, 300);
        // A deleted book (chat card / PAI tool, #428) is removed IMMEDIATELY — the
        // debounced refetch below only reconciles, and it is the slow part.
        const handleCreated = (e) => {
            const d = e?.detail || {};
            if ((d.reason === 'book_deleted' || d.type === 'book_deleted') && d.book_id) {
                removeBookLocally(d.book_id);
            }
            // A renamed book (#464) shows its new name at once (tab + rows).
            if (d.reason === 'book_renamed' && d.book_id) applyLiveState(renameBook, d);
            // A move / re-classification / recurrence change. The `type` guard keeps
            // this from also reacting to the `-updated` twin of a DELETE (that one is
            // applied by handleDeleted) and to bare refresh pings that carry no data.
            if (d.type === 'expense_updated') applyLiveState(updateExpense, d);
            debounced();
        };
        const handleDeleted = (e) => {
            const d = e?.detail || {};
            const id = d.id ?? d.expense_id;
            // Exactly-once: the same delete can arrive as the in-stream event, its
            // `-updated` twin and a live-feed echo; counts must only move once.
            if (id && !appliedDeletesRef.current.has(id)) {
                appliedDeletesRef.current.add(id);
                if (appliedDeletesRef.current.size > 500) appliedDeletesRef.current = new Set([id]);
                applyLiveState(removeExpense, d);
            }
            debounced();
        };
        window.addEventListener('omnis:expense-created', handleCreated);
        window.addEventListener('omnis:expense-updated', handleCreated);
        window.addEventListener('omnis:expense-deleted', handleDeleted);
        return () => {
            debounced.cancel();
            window.removeEventListener('omnis:expense-created', handleCreated);
            window.removeEventListener('omnis:expense-updated', handleCreated);
            window.removeEventListener('omnis:expense-deleted', handleDeleted);
        };
    }, [fetchBooks, fetchExpenses, selectedKey, removeBookLocally, applyLiveState]);

    const handleCreateBook = async (name) => {
        const book = await expenseService.createBook({ name });
        await fetchBooks();
        setSelectedKey(book.id);
    };

    const handleAssign = async (expense, bookId) => {
        const body = bookId ? { book_id: bookId } : { book_id: 'unassigned' };
        try {
            await expenseService.updateExpense(expense.id, body);
            await fetchBooks();
            await fetchExpenses(selectedKey);
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
    };

    const handleDragStart = (expense) => setDraggingExpense(expense);
    const handleDragEnd = () => { setDraggingExpense(null); setDragOverTab(null); };
    const handleTabDragOver = (tabKey) => (e) => {
        if (!draggingExpense) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverTab !== tabKey) setDragOverTab(tabKey);
    };
    const handleTabDragLeave = (tabKey) => () => {
        if (dragOverTab === tabKey) setDragOverTab(null);
    };
    const handleTabDrop = (tabKey) => async (e) => {
        e.preventDefault();
        const expenseId = e.dataTransfer.getData(DRAG_TYPE_ID) || draggingExpense?.id;
        setDragOverTab(null);
        setDraggingExpense(null);
        if (!expenseId) return;
        const sameBook =
            (tabKey === UNASSIGNED_KEY && draggingExpense && !draggingExpense.book_id) ||
            (tabKey !== UNASSIGNED_KEY && draggingExpense?.book_id === tabKey);
        if (sameBook) return;
        try {
            const body = tabKey === UNASSIGNED_KEY ? { book_id: 'unassigned' } : { book_id: tabKey };
            await expenseService.updateExpense(expenseId, body);
            await fetchBooks();
            await fetchExpenses(selectedKey);
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
    };

    const handleDelete = async (expense) => {
        // Recurring 2.0 (#234): deleting a recurring TEMPLATE offers the same
        // Eliminate/Keep choice as Stop recurring (the FK is ON DELETE CASCADE, so
        // a plain delete removes every generated occurrence too).
        if (expense.is_recurring) {
            setDeletingTemplate(expense);
            return;
        }
        const choice = await askDelete(
            t('finDeleteExpenseFor', 'Delete expense for {merchant}?', { merchant: expense.merchant || t('finThisReceipt', 'this receipt') }),
            [expense],
        );
        if (!choice) return;
        try {
            await expenseService.deleteExpense(expense.id, { deleteFiles: choice.deleteFiles });
            await fetchBooks();
            setExpenses((prev) => prev.filter((e) => e.id !== expense.id));
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
    };

    // #234 — resolve the recurring-template delete choice (Eliminate / Keep).
    const confirmDeleteTemplate = async (mode) => {
        const tpl = deletingTemplate;
        setDeletingTemplate(null);
        if (!tpl) return;
        try {
            await expenseService.deleteExpense(tpl.id, {
                occurrences: mode,
                deleteFiles: !!(templateDeleteFiles && tpl.document_id),
            });
            await fetchBooks();
            await fetchExpenses(selectedKey); // occurrences were removed or detached
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
    };

    // Recurring 2.0 "Universal Recurrence Control" (#234 re-open): resolve the
    // template that owns a lineage row's rule, so any ticket in the series can
    // open (and Stop) it. The loaded ledger covers the normal case — a generated
    // occurrence inherits the template's book — and the account-wide lookup is
    // the fallback for the edge where the template was later moved to another
    // book. Returns null when the rule no longer exists (template deleted).
    const resolveRuleTemplate = useCallback(async (ruleId) => {
        if (!ruleId) return null;
        const local = expenses.find((e) => e.id === ruleId);
        if (local) return local;
        const res = await expenseService.listExpenses({});
        return (res.expenses || []).find((e) => e.id === ruleId) || null;
    }, [expenses]);

    const handleUpdateExpense = async (expenseId, updates) => {
        const updated = await expenseService.updateExpense(expenseId, updates);
        // Optimistic local merge so the card stops showing stale text the
        // moment the request resolves; refresh books so totals match the
        // (possibly converted) new amount.
        setExpenses((prev) => prev.map((e) => (e.id === expenseId ? { ...e, ...(updated || updates) } : e)));
        await fetchBooks();
        // Recurring 2.0 (#234): a recurrence edit creates/deletes/detaches
        // occurrence rows (replace/keep_both/eliminate/keep + lazy backfill), so
        // the optimistic single-row merge isn't enough — reload the ledger.
        if (updates && ('recurrence' in updates
            || 'occurrence_cleanup' in updates
            || 'recurrence_anchor_date' in updates)) {
            await fetchExpenses(selectedKey);
        }
    };

    const currentBook = useMemo(() => books.find((b) => b.id === selectedKey), [books, selectedKey]);

    // ── Options menu placement (Options layout finance #249) ────────────────────
    // The menu used to be an `absolute right-0` child of the Net-balance header.
    // Inside a docked/split Finance panel that box is narrow and its ancestors clip
    // overflow, so a w-56 (224px) menu anchored to the right edge ran past the
    // panel's LEFT edge and was SLICED — the PO saw "New entry"/"Select"/"Manage
    // Members" cut in half and the rest "limping behind" the Main Chat. Positioning
    // it in the panel can't fix that: the clip is done by an ancestor.
    // Fix = the DayCard kebab pattern — render into a document.body portal at fixed
    // viewport coords, right-aligned to the trigger but CLAMPED so the left edge
    // stays on screen, and flipped upward when there isn't room below.
    const computeOptionsPos = useCallback(() => {
        const r = optionsBtnRef.current?.getBoundingClientRect();
        if (!r) return null;
        // Keep both edges on screen: never less than 8px from the right, and
        // never so far right that the fixed-width box overflows the left edge.
        const maxRight = Math.max(OPTIONS_MENU_EDGE, window.innerWidth - OPTIONS_MENU_WIDTH - OPTIONS_MENU_EDGE);
        const right = Math.min(maxRight, Math.max(OPTIONS_MENU_EDGE, Math.round(window.innerWidth - r.right)));
        // Vertically: open below when the whole menu fits there; otherwise take
        // whichever side has more room and CAP the box to it (it scrolls), so the
        // menu can never run off the bottom — or off the top when flipped.
        const roomBelow = window.innerHeight - r.bottom - OPTIONS_MENU_GAP - OPTIONS_MENU_EDGE;
        const roomAbove = r.top - OPTIONS_MENU_GAP - OPTIONS_MENU_EDGE;
        return roomBelow >= OPTIONS_MENU_EST_HEIGHT || roomBelow >= roomAbove
            ? { top: Math.round(r.bottom + OPTIONS_MENU_GAP), right, maxHeight: Math.round(Math.max(0, roomBelow)) }
            : { bottom: Math.round(window.innerHeight - r.top + OPTIONS_MENU_GAP), right, maxHeight: Math.round(Math.max(0, roomAbove)) };
    }, []);

    const openOptions = () => {
        if (optionsOpen) { setOptionsOpen(false); return; }
        setOptionsPos(computeOptionsPos());
        setOptionsOpen(true);
    };

    // Close on outside click, and on scroll/resize so the portaled box never
    // detaches from a moving trigger. The menu lives in a body portal, so the
    // outside-click test must consult BOTH the trigger and the portal node.
    useEffect(() => {
        if (!optionsOpen) return undefined;
        const onDown = (e) => {
            if (optionsBtnRef.current?.contains(e.target)) return;
            if (optionsMenuRef.current?.contains(e.target)) return;
            setOptionsOpen(false);
        };
        // RE-ANCHOR (don't close) on scroll/resize, so the portaled box keeps
        // tracking its trigger. Closing on any capture-phase scroll was too
        // aggressive: the ledger fires scroll events while its data loads, which
        // tore the freshly-opened menu out of the DOM mid-click. Two exclusions:
        // the menu's OWN internal scrolling (it scrolls when capped to the
        // available room), and a trigger that has gone away entirely.
        const onReflow = (e) => {
            if (e?.target && optionsMenuRef.current?.contains(e.target)) return;
            const next = computeOptionsPos();
            if (!next) { setOptionsOpen(false); return; }
            setOptionsPos((prev) => (
                prev && prev.top === next.top && prev.bottom === next.bottom
                    && prev.right === next.right && prev.maxHeight === next.maxHeight
                    ? prev // unchanged → no re-render (keeps the box perfectly stable)
                    : next
            ));
        };
        document.addEventListener('mousedown', onDown);
        window.addEventListener('resize', onReflow);
        window.addEventListener('scroll', onReflow, true); // capture: any ancestor scroll
        return () => {
            document.removeEventListener('mousedown', onDown);
            window.removeEventListener('resize', onReflow);
            window.removeEventListener('scroll', onReflow, true);
        };
    }, [optionsOpen, computeOptionsPos]);

    // Rename the active book (Options → Rename book). Owner-only at the route layer.
    const handleRenameBook = async (newName) => {
        const trimmed = (newName || '').trim();
        if (!currentBook || !trimmed) return;
        await expenseService.updateBook(currentBook.id, { name: trimmed });
        await fetchBooks();
        setShowRename(false);
    };
    // Eliminate the active book AND all its transactions (Options → Eliminate book).
    // After deletion we fall back to the Unassigned tab.
    // Set (or clear, with '') the selected book's base currency (#250). Every
    // transaction keeps the currency it was captured in — only the unit the book
    // AGGREGATES into changes, so this is a re-read, never a data rewrite.
    const handleSetBookCurrency = async (code) => {
        if (!currentBook) return;
        try {
            await expenseService.updateBook(currentBook.id, { base_currency: code || null });
            await fetchBooks();
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
    };

    // Eliminate book — OPTIMISTIC (Deletion Finance bug #428). It used to
    // `await deleteBook()` and then `await fetchBooks()` before the tab list
    // changed, i.e. the book stayed on screen for as long as the books GET took —
    // which is slow by nature (it converts every transaction's currency
    // server-side and can block on an upstream FX rate). The book is now taken out
    // of local state the instant the user confirms; the refetch only RECONCILES.
    // A failed delete puts it back exactly where it was and says why.
    const handleEliminateBook = ({ deleteFiles = false } = {}) => {
        if (!currentBook) return;
        const target = currentBook;
        setBookDeleteError(null);
        const index = books.findIndex((b) => b.id === target.id);
        const wasSelected = selectedKey === target.id;
        setShowEliminate(false);
        removeBookLocally(target.id);
        expenseService.deleteBook(target.id, { deleteTransactions: true, deleteFiles })
            .then(() => { fetchBooks(); })
            .catch((err) => {
                // A 404 means it was already gone — the optimistic state is right.
                if (err?.status === 404) { fetchBooks(); return; }
                booksReqRef.current += 1;   // drop any reconcile fetched before the failure
                setBooks((prev) => {
                    if (prev.some((b) => b.id === target.id)) return prev;
                    const next = prev.slice();
                    next.splice(index < 0 ? next.length : Math.min(index, next.length), 0, target);
                    return next;
                });
                if (wasSelected) setSelectedKey(target.id);
                setBookDeleteError(userErrorMessage(err, t));
                fetchBooks();
            });
    };

    // Headline figures come straight from the book's own stats (#196/#250).
    //
    // They used to come from an all-time GET /api/expenses/summary call, with the
    // raw book stats as a fallback — and BOTH sides of that were wrong:
    //   * the fallback was `SUM(amount)` with no currency awareness, so a
    //     THB+EUR book rendered a 1:1 sum wearing the display-currency symbol
    //     (the PO's "€1,100");
    //   * /summary filters on expense_date, so a book whose rows are UNDATED
    //     produced no bucket at all and silently fell through to that fallback.
    // The books endpoint now converts server-side, carries the unit its figures
    // are in, and applies no date filter — so it is simply the right source for
    // "this book's balance", and the summary fetch is gone.
    // The stats block backing the header — the selected book, or Unassigned.
    const currentBookStats = selectedKey === UNASSIGNED_KEY ? unassignedStats : currentBook;

    const currentTotals = useMemo(() => {
        const raw = selectedKey === UNASSIGNED_KEY ? unassignedStats : currentBook;
        // The net is DERIVED from the income/expense printed beneath it
        // (user-testing hotfix 2026-10-06 — see utils/bookTotals), so the card
        // can never show a balance that contradicts its own breakdown. The
        // currency is the unit the SERVER expressed the figures in (the book's
        // base currency when it has one, otherwise the viewer's preference).
        return bookTotals(raw, displayCurrency);
    }, [selectedKey, currentBook, unassignedStats, displayCurrency]);

    /* ── Finance book Ui (#380) — fitting the Net Balance figure ──────────────
       ORIGINAL BUG: the figure was a FIXED 44px span with no width guard, so a
       large total simply overflowed the card into the neighbouring occupant.

       RE-OPENED (PO, 2026-09-24): "the split-card header totals prevent the text
       from physically bleeding into adjacent features, but the layout remains
       broken… when the UI space is minimized to the maximum floor the visual
       presentation collapses completely". The first fix bought containment with
       `[overflow-wrap:anywhere]` plus ONE font-size step (44px → 28px below a
       360px card). Containment held; legibility did not. Measured on production:

         • The step is BINARY, so it cannot know how wide the string actually is.
           A 9-digit total needs ~437px at 44px but the step only engages below a
           ~464px panel — every width in between renders 44px into ~250px of room
           and breaks the number MID-VALUE ("+US$124,362,50 / 9.47").
         • Even at 28px the same total needs ~272px while the figure's row only
           had ~250px (the Options cluster took the rest), so at the 380px panel
           floor it STILL broke — "+US$124,36 / 2,509.47", a thousands group
           severed. A money figure split across lines is misread, not merely ugly.

       THE FIX, three parts:
         1. The figure is `whitespace-nowrap` — it may never break. (The sign and
            the digits are one unit too; `anywhere` used to orphan a lone "−".)
         2. It owns the FULL card width: the Options cluster is out of flow now.
         3. Its font size is SOLVED from the measured text instead of stepped:
            width scales linearly with font-size (the `tracking` is in `em`), so
            measuring the rendered string once gives the exact size at which it
            fills the available width. Clamped to [NET_FIGURE_MIN_PX,
            NET_FIGURE_MAX_PX].

       At the 380px floor that means ~276px of room: an ordinary total keeps the
       full 44px (better than the old blanket 28px) and the largest value the
       column can even store lands well above the 16px floor, on ONE line.
       `overflow-hidden` on the card remains the ultimate backstop.
       (⚠️ That "largest storable value" is no longer 9,999,999,999.99 — #405
       widened the column to NUMERIC(24,2). The solver is unaffected: it measures
       the string it is actually given, whatever its magnitude.)

       THIRD RE-OPEN (PO, 2026-09-28): "the Net Balance fix works, but the
       adjacent Income and Expenses split-card header figures still bleed
       drastically out of their boxes." Correct, and the previous pass is what
       made it visible: the split totals were given `whitespace-nowrap` + a
       WRAPPING header (so a big total drops to its own line) but kept a FIXED
       19px, and `.pai-card` carries no `overflow-hidden`. `nowrap` cannot wrap
       and 19px cannot shrink, so the only thing left for the text to do was
       leave the card. The old spec missed it because its containment assertion
       was panel-level: a figure could overrun its CARD by 20px and still sit
       inside the panel. #405 (NUMERIC(12,2) → NUMERIC(24,2)) then made the
       overrun big enough to clear the panel too.

       So the solve is no longer bespoke to the headline figure: all three
       money figures go through the SAME `useFittedFigure` hook. Their only
       difference is the [min, max] band their design allows. */
    const NET_FIGURE_MAX_PX = 44;
    const NET_FIGURE_MIN_PX = 16;
    // The split totals are a 19px secondary figure in a column that is HALF the
    // panel at the 380px floor (~123px of content), so they need a lower floor
    // than the headline: a trillion-scale THB total is ~218px at 19px and has to
    // reach ~11px to fit on one line there. 11px is small, and that is the
    // honest trade — the PO rejected bleeding, and truncating a money figure is
    // worse than shrinking it. It is only reached at the floor with BOTH columns
    // populated AND a 13-digit value; one-sided books get the full column width
    // and ordinary totals keep the full 19px.
    const SPLIT_TOTAL_MAX_PX = 19;
    const SPLIT_TOTAL_MIN_PX = 11;

    // Container-driven stacking of the Expenses/Income cards (see the comment at
    // the split row). 560px is `NotesPanel`'s own two-column rule, measured on the
    // ledger body — the same thing that file measures.
    const SPLIT_STACK_WIDTH = 560;
    const [ledgerBodyEl, setLedgerBodyEl] = useState(null);
    const ledgerBodyRef = useCallback((node) => setLedgerBodyEl(node), []);
    const [splitStacked, setSplitStacked] = useState(false);
    useEffect(() => {
        if (!ledgerBodyEl || typeof ResizeObserver === 'undefined') return undefined;
        const apply = (w) => setSplitStacked((prev) => (prev === (w < SPLIT_STACK_WIDTH) ? prev : w < SPLIT_STACK_WIDTH));
        apply(ledgerBodyEl.clientWidth);
        const ro = new ResizeObserver(() => apply(ledgerBodyEl.clientWidth));
        ro.observe(ledgerBodyEl);
        return () => ro.disconnect();
    }, [ledgerBodyEl]);

    const netFit = useFittedFigure({
        maxPx: NET_FIGURE_MAX_PX, minPx: NET_FIGURE_MIN_PX, deps: [currentTotals, t],
    });
    // #481 (re-open): the live money figures are KEYED on their own text, so a
    // new value mounts a NEW element instead of rewriting the old text node.
    // `translate="no"` alone did not survive real Chrome: its page translator
    // still replaced the net figure's text nodes with copies, React kept writing
    // into the detached originals, and the PO saw a red "+0,00 €" above a correct
    // "7,21 € − 0,00 €". A remounted node carries the right value whatever a
    // translator did to the previous one. The Expenses/Income totals, counts,
    // the breakdown line and the card amounts use the same rule.
    // "Format with the unit the SERVER expressed these figures in" (the book's
    // base currency when it has one) — never the global preference.
    const netText = `${currentTotals.net >= 0 ? '+' : '−'}${formatAmount(Math.abs(currentTotals.net), currentTotals.currency)}`;
    const incomeText = formatAmount(currentTotals.income, currentTotals.currency);
    const expenseText = formatAmount(currentTotals.expense, currentTotals.currency);
    // Simplify currencies (#425): the split totals are formatted in
    // `currentTotals.currency` — the SAME unit the Net Balance is in (the book's
    // base currency, else the viewer's preference for Unassigned) — never the
    // global preference, which used to put a "$" on a EUR book's raw figure.
    const expenseTotalFit = useFittedFigure({
        maxPx: SPLIT_TOTAL_MAX_PX, minPx: SPLIT_TOTAL_MIN_PX,
        deps: [currentTotals, t],
    });
    const incomeTotalFit = useFittedFigure({
        maxPx: SPLIT_TOTAL_MAX_PX, minPx: SPLIT_TOTAL_MIN_PX,
        deps: [currentTotals, t],
    });

    // Split the current list into the two columns.
    const expensesByDirection = useMemo(() => {
        const out = { expense: [], income: [] };
        for (const e of expenses) {
            (e.direction === 'income' ? out.income : out.expense).push(e);
        }
        return out;
    }, [expenses]);

    // Members can edit/delete only the transactions they created; owners and
    // the user's own Unassigned rows are always modifiable.
    const expenseCanModify = useCallback((expense) => {
        if (!expense?.book_id) return true; // Unassigned — backend already scopes by creator
        const book = books.find((b) => b.id === expense.book_id);
        if (!book) return false;
        if (book.viewer_role === 'owner') return true;
        return expense.created_by_user_id === authUser?.id;
    }, [books, authUser?.id]);

    // Consolidated top-right overflow menu (Financial Cleanup #77): a single
    // three-dot icon replacing the standalone Export + New Book buttons. It holds
    // New Book, Export, and the relocated Unassigned Book view (Unassigned was
    // removed from the book-tab list so system-level items don't mix with user
    // ledgers). The panel header then keeps only this menu + the expansion arrows
    // + the X close (provided by PanelShell).
    const headerMenuItem = 'w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-sm text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors';
    const headerExtras = (
        <div className="relative">
            <button
                onClick={() => setHeaderMenuOpen((v) => !v)}
                className="pai-icon-btn w-[38px] h-[38px]"
                title={t('finMenuTitle', 'Menu')}
                aria-haspopup="menu"
                aria-expanded={headerMenuOpen}
                aria-label={t('finFinanceMenuAriaLabel', 'Finance menu')}
                ref={headerMenuBtnRef}
                data-testid="expenses-header-menu-button"
            >
                <MoreVertical size={18} />
            </button>
            {headerMenuOpen && headerMenuPos && createPortal(
                <>
                    <div
                        ref={headerMenuRef}
                        role="menu"
                        data-testid="expenses-header-menu"
                        className="fixed z-[61] w-52 bg-pai-modal border border-pai rounded-ctl shadow-modal-sm py-1.5"
                        style={{ top: headerMenuPos.top, right: headerMenuPos.right }}
                    >
                        <button role="menuitem" className={headerMenuItem} onClick={() => { setHeaderMenuOpen(false); setShowNewBook(true); }}>
                            <Plus size={16} /> {t('finNewBook', 'New Book')}
                        </button>
                        <button role="menuitem" className={headerMenuItem} onClick={() => { setHeaderMenuOpen(false); setShowExport(true); }}>
                            <Download size={16} /> {t('finExport', 'Export')}
                        </button>
                        <button role="menuitem" className={`${headerMenuItem} justify-between`} onClick={() => { setHeaderMenuOpen(false); setSelectedKey(UNASSIGNED_KEY); }}>
                            <span className="flex items-center gap-2"><Inbox size={16} /> {t('finUnassignedBook', 'Unassigned Book')}</span>
                            {selectedKey === UNASSIGNED_KEY && <Check size={14} data-testid="book-active-check" className="text-pai-green" />}
                        </button>
                    </div>
                </>,
                document.body,
            )}
        </div>
    );

    return (
        <PanelShell
            title={t('expenses', 'Finance')}
            icon={<Wallet size={20} className="text-white" />}
            mode={mode}
            onClose={onClose}
            onToggleExpand={onToggleExpand}
            headerExtras={headerExtras}
        >
            {/* Tabs — Unassigned was relocated to the top-right menu (Financial
                Cleanup #77), so the tab strip lists ONLY user books. */}
            <div className="border-b border-pai-hairline px-7">
                <div data-testid="expenses-book-tabs" className="flex items-center gap-1 overflow-x-auto">
                    {books.map((book) => (
                        <TabButton
                            key={book.id}
                            active={selectedKey === book.id}
                            onClick={() => { setBookDeleteError(null); setSelectedKey(book.id); }}
                            label={book.name}
                            count={(book.expense_count || 0) + (book.income_count || 0)}
                            isDropTarget={!!draggingExpense}
                            isDraggingOver={dragOverTab === book.id}
                            onDragOver={handleTabDragOver(book.id)}
                            onDragEnter={handleTabDragOver(book.id)}
                            onDragLeave={handleTabDragLeave(book.id)}
                            onDrop={handleTabDrop(book.id)}
                        />
                    ))}
                </div>
            </div>

            {/* Net Balance card — the handoff's headline block. The card itself is a
                neutral pane: the accent lives on the FIGURE only, never as a wash
                behind the whole header (the accent rule).

                Finance book Ui (#380): the figure row below carries
                `netFit.containerRef` (the width the size is solved against);
                `overflow-hidden` here is the ultimate structural backstop
                (belt-and-suspenders alongside the wrap/shrink classes below) —
                the card's own edge is where a number must stop, no matter how
                large. */}
            <div className="px-7 pt-5 pb-1">
              <div data-testid="ledger-net-balance" className="pai-card px-6 py-5 min-w-0 max-w-full overflow-hidden">
                {/* #380 (RE-OPEN): the Options cluster is POSITIONED, not a flex
                    sibling. As a `flex-shrink-0` column it took ~88px + a 12px gap
                    out of the card's content box, so at the 380px panel floor the
                    figure was solving for ~250px of a 272px card — which is what
                    made a perfectly ordinary nine-digit total break mid-number.
                    Out of flow, the figure below owns the FULL card width. */}
                <div className="relative">
                    {/* ⚠️ currency-net-balance.spec.js does
                        `getByText('Net balance').locator('..').innerText()` and regex-parses
                        the amount out of it — so the label's PARENT must stay an ancestor of
                        the figure. Keep the Options control OUTSIDE this block. */}
                    <div className="min-w-0 max-w-full">
                        <span className="block text-[17px] font-bold text-white pr-24 truncate">
                            {t('finNetBalance', 'Net balance')}
                        </span>
                        <div ref={netFit.containerRef} className="mt-1.5 flex items-baseline gap-2.5 flex-wrap min-w-0">
                            {/* #380 (RE-OPEN) — `whitespace-nowrap` + a FITTED font size.
                                A money figure that breaks across lines is not
                                "contained", it is mangled: the PO's report shows
                                "+US$124,36 / 2,509.47", a thousands group split in
                                half. Nothing may wrap here, and `netFit.fontPx`
                                is solved from the real measured text so ONE line
                                is always achievable (see `useFittedFigure`). */}
                            {/* #481: every live money figure here is `translate="no"`.
                                A browser auto-translate (Google Translate) swaps
                                React's text nodes for its own <font> copies, so
                                every later update lands on a detached node and
                                the screen keeps the figure it translated — the
                                "+0,00 €" (red: the class still updated) above
                                "0,00 € − 202,49 €". Whether translation ran
                                before or after the books loaded decided "zero"
                                vs "right" on each reload. Numbers never need
                                translating. */}
                            <span
                                key={netText}
                                translate="no"
                                ref={netFit.figureRef}
                                data-testid="ledger-net-figure"
                                style={{ fontSize: `${netFit.fontPx}px` }}
                                className={`tnum min-w-0 max-w-full whitespace-nowrap leading-none font-extrabold tracking-[-0.035em] ${
                                currentTotals.net >= 0 ? 'text-pai-green' : 'text-pai-red'
                        }`}>
                            {netText}
                        </span>
                        {/* Per-BOOK now (#250). This used to read an account-wide
                            flag, so a single-currency book showed "(converted)"
                            whenever any *other* book was mixed. */}
                        {currentBookStats?.mixed_currencies && (
                            <span
                                data-testid="ledger-converted-note"
                                    className="text-[15px] font-medium text-pai-meta"
                            >
                                {t('finConvertedNote', '(converted)')}
                            </span>
                        )}
                        {currentTotals.unconverted > 0 && (
                            <span
                                data-testid="ledger-unconverted-note"
                                title={t('finUnconvertedTitle', 'No exchange rate was available for these transactions, so they are listed but left out of the balance.')}
                                    className="text-[13.5px] font-medium text-pai-amber"
                            >
                                {t('finUnconvertedCount', '({n} unconverted)', { n: currentTotals.unconverted })}
                            </span>
                        )}
                        </div>
                        {/* #380 (RE-OPEN): this line used to carry
                            `[overflow-wrap:anywhere]`, which kept it inside the card
                            by breaking a currency string wherever it ran out of room
                            — "US$124,362,509.47 −" / "US$0.00" in the PO's capture.
                            It is a flex row now, so it breaks BETWEEN the two figures
                            (the only honest break point) and each figure is
                            `whitespace-nowrap`. At 14.5px even the largest storable
                            amount is ~130px, so a single figure always fits. */}
                        <div translate="no" data-testid="ledger-net-breakdown" className="mt-2.5 flex flex-wrap items-baseline gap-x-1.5 text-[14.5px] text-pai-meta min-w-0">
                            <span key={`i${incomeText}`} className="tnum text-pai-green whitespace-nowrap">
                                {incomeText}
                            </span>
                            <span aria-hidden="true">{'−'}</span>
                            <span key={`e${expenseText}`} className="tnum text-pai-red whitespace-nowrap">
                                {expenseText}
                            </span>
                        </div>
                    </div>
                    {/* All book-level actions consolidated into a single "Options"
                        dropdown (Ledger UI Reorganization ticket) — replaces the old
                        horizontal row of Select / New / Manage / Currency buttons.
                        #380: out of flow (see the note on the wrapper above), pinned
                        to the card's top-right exactly where it rendered before. */}
                    <div className="absolute top-0 right-0 flex items-center gap-1.5">
                        <div className="relative">
                            <button
                                ref={optionsBtnRef}
                                data-testid="book-options-button"
                                onClick={openOptions}
                                className="flex items-center gap-1.5 px-2 py-1 text-[13.5px] font-medium text-pai-meta hover:text-white transition-colors rounded-chip"
                                title={t('finBookOptionsTitle', 'Book options')}
                                aria-haspopup="menu"
                                aria-expanded={optionsOpen}
                            >
                                {t('finOptions', 'Options')}
                                <MoreHorizontal size={16} />
                            </button>
                            {/* #249: portaled to <body> at fixed viewport coords (see
                                openOptions) — an ancestor's overflow/stacking context can
                                no longer clip it or bury it under the Main Chat. */}
                            {optionsOpen && optionsPos && createPortal(
                                <>
                                    {/* #500 re-open: no full-screen click-catcher here — it locked page
                                        scrolling. The outside-press + re-anchor effect near openOptions
                                        closes / follows it. */}
                                    <div
                                        ref={optionsMenuRef}
                                        role="menu"
                                        data-testid="book-options-menu"
                                        style={{
                                            position: 'fixed',
                                            top: optionsPos.top,
                                            bottom: optionsPos.bottom,
                                            right: optionsPos.right,
                                            width: OPTIONS_MENU_WIDTH,
                                            maxHeight: optionsPos.maxHeight,
                                        }}
                                        className="z-[101] overflow-y-auto bg-pai-modal border border-pai rounded-ctl shadow-modal-sm py-1.5 text-sm"
                                    >
                                        <button
                                            role="menuitem"
                                            onClick={() => { setOptionsOpen(false); setShowManualEntry(true); }}
                                            className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors"
                                        >
                                            <Plus size={14} /> {t('finNewEntry', 'New entry')}
                                        </button>
                                        {expenses.length > 0 && (
                                            <button
                                                role="menuitem"
                                                onClick={() => { setOptionsOpen(false); setSelectionMode((v) => !v); }}
                                                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors"
                                            >
                                                {selectionMode ? <CheckSquare size={14} /> : <Square size={14} />} {selectionMode ? t('finExitSelection', 'Exit selection') : t('select', 'Select')}
                                            </button>
                                        )}
                                        {selectedKey !== UNASSIGNED_KEY && currentBook && (
                                            <button
                                                role="menuitem"
                                                onClick={() => { setOptionsOpen(false); setShowMembers(true); }}
                                                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors"
                                            >
                                                <Users size={14} /> {currentBook.viewer_role === 'owner' ? t('finManageMembers', 'Manage Members') : t('finMembersWord', 'Members')}
                                            </button>
                                        )}
                                        {/* Currency (#250, box fixed by #364). On a BOOK this
                                            sets that book's own base currency — the unit it
                                            aggregates every transaction into, shared by every
                                            member and every surface — instead of silently
                                            flipping a browser-local display preference.
                                            "Follow my preference" clears it back to the
                                            pre-#250 behaviour. Unassigned has no book row, so
                                            it keeps the preference.

                                            #364: the CLOSED box must show the CURRENCY
                                            CURRENTLY IN USE (a 3-letter code), never the
                                            "Follow my preference (…)" sentence — a native
                                            <select> renders its selected OPTION's own text in
                                            the closed box, and that sentence is too long for
                                            this chip, so it rendered clipped mid-word
                                            ("follow my prefer"). A native <option> can't carry
                                            different text for the closed box vs the open list,
                                            so the effective code is rendered in a separate,
                                            purely visual chip UNDER the real <select> — which
                                            stays fully functional (same value/onChange/testid)
                                            but is made invisible (opacity-0) and stretched to
                                            cover the chip, so a click on the code still opens
                                            the OS-native dropdown listing every currency PLUS
                                            the descriptive follow-preference option. */}
                                        <div className="px-3.5 py-2 flex items-center justify-between gap-2">
                                            <span className="flex items-center gap-2.5 text-pai-secondary">
                                                <Wallet size={14} /> {t('commonCurrency', 'Currency')}
                                            </span>
                                            {selectedKey !== UNASSIGNED_KEY && currentBook ? (
                                                <div className="relative" onClick={(e) => e.stopPropagation()}>
                                                    <div
                                                        aria-hidden="true"
                                                        data-testid="book-currency-display"
                                                        title={currentBook.base_currency
                                                            ? currentBook.base_currency
                                                            : t('finFollowMyPreference', 'Follow my preference ({currency})', { currency: displayCurrency })}
                                                        className="pai-input text-[11.5px] px-2 py-1 touch:min-h-[40px] touch:flex touch:items-center rounded-chip [color-scheme:dark] whitespace-nowrap overflow-hidden pointer-events-none"
                                                    >
                                                        {currentBook.base_currency || displayCurrency}
                                                    </div>
                                                    <select
                                                        data-testid="book-base-currency"
                                                        value={currentBook.base_currency || ''}
                                                        disabled={currentBook.viewer_role !== 'owner'}
                                                        onChange={(e) => handleSetBookCurrency(e.target.value)}
                                                        aria-label={t('commonCurrency', 'Currency')}
                                                        className="absolute inset-0 w-full h-full opacity-0 [color-scheme:dark] disabled:cursor-not-allowed"
                                                    >
                                                        <option value="">{t('finFollowMyPreference', 'Follow my preference ({currency})', { currency: displayCurrency })}</option>
                                                        {supportedCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                </div>
                                            ) : (
                                                <select
                                                    data-testid="display-currency"
                                                    value={displayCurrency}
                                                    // Synced with Settings → Currency (#480); no "Saved" toast here.
                                                    onChange={(e) => { Promise.resolve(setDisplayCurrency(e.target.value)).catch(() => { /* reverted by the hook */ }); }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="pai-input text-[11.5px] px-2 py-1 rounded-chip [color-scheme:dark]"
                                                >
                                                    {supportedCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                            )}
                                        </div>
                                        {/* Book Settings (Rename) — any Owner. Eliminate (delete) —
                                            OG Creator ONLY (#195). */}
                                        {selectedKey !== UNASSIGNED_KEY && currentBook?.viewer_role === 'owner' && (
                                            <>
                                                <div className="my-1.5 border-t border-pai-hairline" />
                                                <button
                                                    role="menuitem"
                                                    onClick={() => { setOptionsOpen(false); setShowRename(true); }}
                                                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors"
                                                >
                                                    <Pencil size={14} /> {t('finRenameBookMenu', 'Rename book')}
                                                </button>
                                            </>
                                        )}
                                        {selectedKey !== UNASSIGNED_KEY && currentBook && currentBook.created_by === authUser?.id && (
                                            <button
                                                role="menuitem"
                                                data-testid="book-eliminate"
                                                onClick={() => { setOptionsOpen(false); setShowEliminate(true); }}
                                                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-pai-red hover:bg-[rgba(240,68,56,0.12)] transition-colors"
                                            >
                                                <Trash2 size={14} /> {t('finEliminateBookMenu', 'Eliminate book')}
                                            </button>
                                        )}
                                    </div>
                                </>,
                                document.body,
                            )}
                        </div>
                    </div>
                </div>
            </div>
            </div>

            {/* View toggle (Finance Model #442): the detailed ledger ⇄ a graphical
                summary of the same book. A two-segment control rather than one
                flip button, so the current view is always visible. */}
            <div className="px-7 pt-3 flex items-center min-w-0">
                <div
                    role="group"
                    aria-label={t('finVizToggleLabel', 'View')}
                    data-testid="finance-view-toggle"
                    data-mode={viewMode}
                    className="flex items-center gap-1 p-1 rounded-ctl border border-pai-subtle bg-white/[0.03] min-w-0"
                >
                    <button
                        type="button"
                        data-testid="finance-view-list"
                        aria-pressed={viewMode === 'list'}
                        onClick={() => setViewMode('list')}
                        className={`pai-tap-row flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-seg transition-colors min-w-0 ${
                            viewMode === 'list' ? 'bg-white/[0.1] text-white' : 'text-pai-meta hover:text-white'
                        }`}
                    >
                        <ListIcon size={14} className="flex-shrink-0" />
                        <span className="truncate">{t('finVizList', 'List')}</span>
                    </button>
                    <button
                        type="button"
                        data-testid="finance-view-charts"
                        aria-pressed={viewMode === 'charts'}
                        onClick={() => { exitSelectionMode(); setViewMode('charts'); }}
                        className={`pai-tap-row flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-seg transition-colors min-w-0 ${
                            viewMode === 'charts' ? 'bg-white/[0.1] text-white' : 'text-pai-meta hover:text-white'
                        }`}
                    >
                        <PieChart size={14} className="flex-shrink-0" />
                        <span className="truncate">{t('finVizCharts', 'Charts')}</span>
                    </button>
                </div>
            </div>

            {viewMode === 'charts' && (
                <div className="px-7 py-4 min-w-0">
                    <FinanceCharts
                        bookKey={selectedKey === UNASSIGNED_KEY ? 'unassigned' : selectedKey}
                        displayCurrency={displayCurrency}
                        formatAmount={formatAmount}
                        revision={expenses}
                    />
                </div>
            )}

            {/* Bidirectional content — Expense column (left) + Income column (right).
                `ledgerBodyRef` drives the #380 container-driven stacking.
                Hidden (not unmounted) in Visualization Mode so its measured width
                and fitted totals are intact on the way back. */}
            <div ref={ledgerBodyRef} className={`px-7 py-4 ${viewMode === 'charts' ? 'hidden' : ''}`}>
                {error && (
                    <div className="border border-[rgba(240,68,56,0.32)] bg-[rgba(240,68,56,0.04)] rounded-row px-4 py-3 text-[13.5px] text-pai-red mb-3">
                        {error}
                    </div>
                )}
                {bookDeleteError && (
                    <div data-testid="book-delete-error" className="border border-[rgba(240,68,56,0.32)] bg-[rgba(240,68,56,0.04)] rounded-row px-4 py-3 text-[13.5px] text-pai-red mb-3">
                        {bookDeleteError}
                    </div>
                )}
                {loading && expenses.length === 0 && (
                    <div className="text-center py-10 text-pai-meta text-[14px]">{t('finLoadingEllipsis', 'Loading…')}</div>
                )}
                {/* Empty state: centred, NO illustration — a bold title over a
                    one-line explanation (design system rule 6). */}
                {!loading && expenses.length === 0 && !error && (
                    <div className="text-center py-14">
                        <p className="text-[18px] font-bold text-white/70">{t('finNothingRecorded', 'Nothing recorded here yet.')}</p>
                        <p className="text-[14.5px] text-white/40 mt-1.5">
                            {t('finSendReceiptOrPayslip', 'Send a receipt or payslip photo in chat to log one.')}
                        </p>
                    </div>
                )}
                {expenses.length > 0 && (() => {
                    const hasExpense = expensesByDirection.expense.length > 0;
                    const hasIncome = expensesByDirection.income.length > 0;
                    // Dynamic column width: if only one side has data, it gets 100%.
                    if (!hasExpense && !hasIncome) return null;
                    // #380 (THIRD RE-OPEN): STACK the two cards at narrow widths.
                    // Shrinking alone could not save this. Measured (Manrope 800,
                    // tabular): "THB 1,234,567,890,123.45" is 248px at 19px, while a
                    // half-width column at the 380px panel floor leaves ~122px of
                    // header — the fitted solve would have to drop to **9.3px**, and
                    // "contained but illegible" is exactly what the PO rejected last
                    // pass. Stacked, that same column gets ~292px and the figure keeps
                    // its FULL 19px. So the fitted size handles the middle band and the
                    // stack handles the floor. Container-driven at 560px, matching
                    // `NotesPanel`'s two-column rule verbatim — never a `md:` viewport
                    // breakpoint, because a docked split panel is narrow on a wide
                    // screen (#142). ⚠️ The measured element's width does NOT depend on
                    // the stacking decision (the row is full-width either way), so this
                    // cannot oscillate.
                    const showBoth = hasExpense && hasIncome && !splitStacked;
                    const colWidth = showBoth ? 'w-1/2' : 'w-full';
                    return (
                        <div
                            data-testid="ledger-split-row"
                            data-stacked={splitStacked ? 'true' : 'false'}
                            className={`flex gap-3.5 items-start transition-all ${splitStacked ? 'flex-col' : ''}`}
                        >
                            {hasExpense && (
                                /* #380 (THIRD RE-OPEN): `overflow-hidden` — `.pai-card`
                                   carries none, which is why a `whitespace-nowrap` total
                                   at a fixed size had nothing stopping it leaving the
                                   card. The fitted size below is the real fix; this is
                                   the structural backstop, exactly as on the net card. */
                                <div className={`${colWidth} min-w-0 overflow-hidden pai-card p-4 space-y-2.5 transition-all`}>
                                    {/* Split-card header: a circular icon outlined in the
                                        semantic colour, the title, the item count, and the
                                        card total right-aligned. */}
                                    {/* #380 (RE-OPEN): the row WRAPS now. The first fix
                                        only stopped the total escaping the card — it did it
                                        by letting the total shrink the title to nothing
                                        ("Inc…") and then breaking itself mid-number. A big
                                        total now drops to its own full-width line instead,
                                        where even the largest storable amount fits on one
                                        line at 19px (~198px), so the title stays readable
                                        and the number stays whole. `min-w-[7rem]` on the
                                        title is what FORCES that wrap rather than letting
                                        `min-w-0` collapse it. */}
                                    {/* #380 (THIRD RE-OPEN): the header ROW is the width
                                        budget the total is fitted against — a total too
                                        wide to sit beside the title wraps onto its own
                                        full-width line, and that line is what it has to
                                        fit. See `useFittedFigure`. */}
                                    <div ref={expenseTotalFit.containerRef} className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 min-w-0">
                                        <span className="w-[38px] h-[38px] flex-shrink-0 rounded-full border-[1.5px] border-pai-red text-pai-red flex items-center justify-center">
                                            <ArrowDown size={18} />
                                        </span>
                                        <span className="min-w-[7rem] flex-1">
                                            <span className="block text-[16px] font-bold text-white truncate">
                                                {t('finExpensesPlural', 'Expenses')}
                                            </span>
                                            <span key={`n${expensesByDirection.expense.length}`} translate="no" data-testid="ledger-split-count" data-direction="expense" className="block tnum text-[12.5px] text-pai-meta">
                                                {expensesByDirection.expense.length}
                                            </span>
                                        </span>
                                        <span
                                            key={expenseText}
                                            translate="no"
                                            ref={expenseTotalFit.figureRef}
                                            data-testid="ledger-split-total"
                                            data-direction="expense"
                                            style={{ fontSize: `${expenseTotalFit.fontPx}px` }}
                                            className="tnum font-extrabold text-white ml-auto max-w-full whitespace-nowrap text-right"
                                        >
                                            {expenseText}
                                        </span>
                                    </div>
                                    {expensesByDirection.expense.map((expense) => (
                                        <ExpenseCard
                                            key={expense.id}
                                            expense={expense}
                                            books={books}
                                            supportedCurrencies={supportedCurrencies}
                                            onAssign={handleAssign}
                                            onDelete={handleDelete}
                                            onUpdate={handleUpdateExpense}
                                            onResolveTemplate={resolveRuleTemplate}
                                            onDragStart={handleDragStart}
                                            onDragEnd={handleDragEnd}
                                            isDragging={draggingExpense?.id === expense.id}
                                            focused={focusExpenseId === expense.id}
                                            canModify={expenseCanModify(expense)}
                                            selectionMode={selectionMode}
                                            selected={selectedIds.has(expense.id)}
                                            onSelectToggle={toggleSelected}
                                            onOpen={setFocusExpense}
                                            onPhotoOpen={setPhotoExpense}
                                        />
                                    ))}
                                </div>
                            )}
                            {hasIncome && (
                                <div className={`${colWidth} min-w-0 overflow-hidden pai-card p-4 space-y-2.5 transition-all`}>
                                    {/* #380 (RE-OPEN / THIRD RE-OPEN): same wrapping,
                                        overflow-clipped, fitted header as Expenses above. */}
                                    <div ref={incomeTotalFit.containerRef} className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 min-w-0">
                                        <span className="w-[38px] h-[38px] flex-shrink-0 rounded-full border-[1.5px] border-pai-green text-pai-green flex items-center justify-center">
                                            <ArrowUp size={18} />
                                        </span>
                                        <span className="min-w-[7rem] flex-1">
                                            <span className="block text-[16px] font-bold text-white truncate">
                                                {t('finIncomePlural', 'Income')}
                                            </span>
                                            <span key={`n${expensesByDirection.income.length}`} translate="no" data-testid="ledger-split-count" data-direction="income" className="block tnum text-[12.5px] text-pai-meta">
                                                {expensesByDirection.income.length}
                                            </span>
                                        </span>
                                        <span
                                            key={incomeText}
                                            translate="no"
                                            ref={incomeTotalFit.figureRef}
                                            data-testid="ledger-split-total"
                                            data-direction="income"
                                            style={{ fontSize: `${incomeTotalFit.fontPx}px` }}
                                            className="tnum font-extrabold text-white ml-auto max-w-full whitespace-nowrap text-right"
                                        >
                                            {incomeText}
                                        </span>
                                    </div>
                                    {expensesByDirection.income.map((expense) => (
                                        <ExpenseCard
                                            key={expense.id}
                                            expense={expense}
                                            books={books}
                                            supportedCurrencies={supportedCurrencies}
                                            onAssign={handleAssign}
                                            onDelete={handleDelete}
                                            onUpdate={handleUpdateExpense}
                                            onResolveTemplate={resolveRuleTemplate}
                                            onDragStart={handleDragStart}
                                            onDragEnd={handleDragEnd}
                                            isDragging={draggingExpense?.id === expense.id}
                                            focused={focusExpenseId === expense.id}
                                            canModify={expenseCanModify(expense)}
                                            selectionMode={selectionMode}
                                            selected={selectedIds.has(expense.id)}
                                            onSelectToggle={toggleSelected}
                                            onOpen={setFocusExpense}
                                            onPhotoOpen={setPhotoExpense}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })()}
            </div>

            <NewBookDialog
                open={showNewBook}
                onClose={() => setShowNewBook(false)}
                onCreate={handleCreateBook}
            />

            <ManualEntryDialog
                open={showManualEntry}
                defaultBook={selectedKey !== UNASSIGNED_KEY ? currentBook : null}
                onClose={() => setShowManualEntry(false)}
                onSaved={() => { fetchBooks(); fetchExpenses(selectedKey); }}
            />

            <ExportDialog
                open={showExport}
                onClose={() => setShowExport(false)}
                books={books}
                defaultBookKey={selectedKey}
                defaultCurrency={displayCurrency}
                supportedCurrencies={supportedCurrencies}
            />

            {showMembers && currentBook && (
                <SharedSpaceDialog
                    surface="book"
                    entityId={currentBook.id}
                    viewerUserId={authUser?.id}
                    viewerRole={currentBook.viewer_role}
                    isCreator={!!currentBook.created_by && currentBook.created_by === authUser?.id}
                    onClose={() => setShowMembers(false)}
                    onChanged={() => { fetchBooks(); }}
                />
            )}

            <RenameBookDialog
                open={showRename}
                currentName={currentBook?.name}
                onClose={() => setShowRename(false)}
                onRename={handleRenameBook}
            />

            <EliminateBookDialog
                open={showEliminate}
                bookId={currentBook?.id}
                bookName={currentBook?.name}
                onClose={() => setShowEliminate(false)}
                onConfirm={handleEliminateBook}
            />

            <ExpenseFocusOverlay
                expense={focusExpense}
                books={books}
                viewerUserId={authUser?.id}
                displayCurrency={displayCurrency}
                onClose={() => setFocusExpense(null)}
                onPhotoOpen={(exp) => setPhotoExpense(exp)}
                onAssign={async (exp, bookId, newDirection) => {
                    try {
                        const updated = await expenseService.updateExpense(exp.id, {
                            book_id: bookId,
                            direction: newDirection || exp.direction,
                        });
                        setExpenses((prev) => prev.map((e) => (e.id === exp.id ? { ...e, ...(updated || {}) } : e)));
                        setFocusExpense((cur) => (cur && cur.id === exp.id ? { ...cur, ...(updated || {}) } : cur));
                        await fetchBooks();
                    } catch (err) {
                        setError(userErrorMessage(err, t));
                    }
                }}
                onDelete={async (exp) => {
                    const choice = await askDelete(
                        t('finDeleteReceiptAt', 'Delete the receipt at {merchant}?', { merchant: exp.merchant || t('finThisTransaction', 'this transaction') }),
                        [exp],
                    );
                    if (!choice) return;
                    try {
                        await expenseService.deleteExpense(exp.id, { deleteFiles: choice.deleteFiles });
                        setExpenses((prev) => prev.filter((e) => e.id !== exp.id));
                        setFocusExpense(null);
                        await fetchBooks();
                    } catch (err) {
                        setError(userErrorMessage(err, t));
                    }
                }}
            />

            <ExpensePhotoLightbox
                expense={photoExpense}
                onClose={() => setPhotoExpense(null)}
            />

            {/* #234: recurring-template delete → Eliminate / Keep the generated entries. */}
            {deletingTemplate && (
                <Modal onClose={() => setDeletingTemplate(null)} zIndex="z-[90]" size="sm" cardTestId="recurring-delete-dialog">
                        <div className="p-5 border-b border-pai-subtle">
                            <h3 className="text-[17px] font-bold text-white">
                                {t('finDeleteRecurringTransaction', 'Delete recurring transaction')}
                            </h3>
                        </div>
                        <div className="p-5 space-y-4">
                            <p className="text-[15px] leading-[1.6] text-pai-body">
                                {t('finRuleStoppedQuestion', 'Rule stopped. Do you want to eliminate all generated duplicates from the ledger, or keep them as individual entries?')}
                            </p>
                            {deletingImpact ? (
                                <div
                                    data-testid="recurring-delete-impact"
                                    className="text-[13px] text-pai-secondary border border-pai-subtle bg-white/[0.025] rounded-ctl p-3 space-y-1.5"
                                >
                                    <p>
                                        {t('finEliminateWillPart1', 'This will eliminate')} <strong>{deletingImpact.occurrence_count}</strong>{' '}
                                        {t('finEliminateWillPart2', 'generated {entryWord} across the whole rule’s history.', { entryWord: deletingImpact.occurrence_count === 1 ? t('finEntrySingular', 'entry') : t('finEntryPlural', 'entries') })}
                                    </p>
                                    <p>
                                        {t('finKeepingOriginalTicket', 'Keeping your original ticket:')}{' '}
                                        <strong>{deletingImpact.merchant || t('finThisTransaction', 'this transaction')}</strong>
                                        {deletingImpact.expense_date ? ` — ${formatSourceDate(deletingImpact.expense_date)}` : ''}.
                                    </p>
                                </div>
                            ) : null}
                            {deletingTemplate.document_id ? (
                                <AttachedFileChoice checked={templateDeleteFiles} onChange={setTemplateDeleteFiles} />
                            ) : null}
                            <div className="flex flex-col gap-2">
                                <button
                                    data-testid="recurring-delete-eliminate"
                                    onClick={() => confirmDeleteTemplate('eliminate')}
                                    className="pai-btn-danger w-full"
                                >
                                    {t('finEliminateGeneratedEntries', 'Eliminate generated entries')}
                                </button>
                                <button
                                    data-testid="recurring-delete-keep"
                                    onClick={() => confirmDeleteTemplate('keep')}
                                    className="pai-btn-secondary w-full"
                                >
                                    {t('finKeepIndividualEntries', 'Keep them as individual entries')}
                                </button>
                                <button
                                    data-testid="recurring-delete-cancel"
                                    onClick={() => setDeletingTemplate(null)}
                                    className="w-full px-4 py-2 text-sm font-semibold text-pai-meta hover:text-white transition-colors rounded-ctl"
                                >
                                    {t('commonCancel', 'Cancel')}
                                </button>
                            </div>
                        </div>
                    </Modal>
            )}

            {/* #498 follow-up: transaction delete with a linked file → keep / delete the file. */}
            {deleteAsk && (
                <Modal onClose={() => settleDeleteAsk(null)} zIndex="z-[90]" size="sm" cardTestId="finance-delete-dialog">
                    <div className="p-5 border-b border-pai-subtle">
                        <h3 className="text-[17px] font-bold text-white">
                            {t('finDeleteTransactionTitle', 'Delete transaction')}
                        </h3>
                    </div>
                    <div className="p-5 space-y-4">
                        <p className="text-[15px] leading-[1.6] text-pai-body">{deleteAsk.message}</p>
                        <AttachedFileChoice checked={deleteAskFiles} onChange={setDeleteAskFiles} />
                        <div className="flex flex-col gap-2">
                            <button
                                data-testid="finance-delete-confirm"
                                onClick={() => settleDeleteAsk({ deleteFiles: deleteAskFiles })}
                                className="pai-btn-danger w-full"
                            >
                                {t('commonDelete', 'Delete')}
                            </button>
                            <button
                                data-testid="finance-delete-cancel"
                                onClick={() => settleDeleteAsk(null)}
                                className="w-full px-4 py-2 text-sm font-semibold text-pai-meta hover:text-white transition-colors rounded-ctl"
                            >
                                {t('commonCancel', 'Cancel')}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            <TransferSummaryCard
                summary={transferSummary}
                onClose={() => setTransferSummary(null)}
                onPreview={(it) => setPhotoExpense(it)}
            />

            {selectionMode && selectedIds.size > 0 && (
                <BulkActionBar
                    count={selectedIds.size}
                    books={books}
                    onCancel={exitSelectionMode}
                    onDelete={handleBulkDelete}
                    onMove={handleBulkMove}
                    busy={bulkBusy}
                />
            )}
        </PanelShell>
    );
};

// Sticky bottom action bar shown when multi-select has at least one row picked.
const BulkActionBar = ({ count, books, onCancel, onDelete, onMove, busy }) => {
    const { t } = useLanguage();
    const [moveOpen, setMoveOpen] = useState(false);
    // #500 re-open: outside press closes — no full-screen catcher locking the scroll.
    const moveWrapRef = useRef(null);
    useMenuDismiss(moveOpen, () => setMoveOpen(false), [moveWrapRef]);
    return (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2.5 bg-pai-modal border border-pai rounded-full shadow-modal px-4 py-2 animate-fade-slide-in">
            <span className="tnum text-[14px] font-semibold text-white">
                {t('finCountSelected', '{count} selected', { count })}
            </span>
            <div className="relative" ref={moveWrapRef}>
                <button
                    onClick={() => setMoveOpen((v) => !v)}
                    disabled={busy}
                    className="text-[13px] font-semibold px-3 py-1.5 rounded-full border border-pai-strong text-white hover:bg-white/[0.1] transition-colors disabled:opacity-50"
                >
                    {t('finMoveToEllipsis', 'Move to…')}
                </button>
                {moveOpen && (
                    <>
                        <div className="absolute bottom-full mb-2 left-0 z-40 w-48 max-h-64 overflow-y-auto bg-pai-modal border border-pai rounded-ctl shadow-modal-sm py-1.5">
                            <button
                                onClick={() => { setMoveOpen(false); onMove(null); }}
                                className="w-full text-left px-3.5 py-2 text-sm text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors"
                            >
                                {t('finUnassigned', 'Unassigned')}
                            </button>
                            <div className="border-t border-pai-hairline my-1.5" />
                            {books.map((b) => (
                                <button
                                    key={b.id}
                                    onClick={() => { setMoveOpen(false); onMove(b.id); }}
                                    className="w-full text-left px-3.5 py-2 text-sm text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors"
                                >
                                    {b.name}
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
            <button
                onClick={onDelete}
                disabled={busy}
                className="text-[13px] font-semibold px-3 py-1.5 rounded-full border border-[rgba(240,68,56,0.4)] bg-[rgba(240,68,56,0.08)] text-pai-red hover:bg-[rgba(240,68,56,0.16)] transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
                <Trash2 size={12} />
                {t('commonDelete', 'Delete')}
            </button>
            <button
                onClick={onCancel}
                disabled={busy}
                className="text-[13px] font-semibold px-3 py-1.5 rounded-full text-pai-meta hover:text-white transition-colors disabled:opacity-50"
            >
                {t('commonCancel', 'Cancel')}
            </button>
        </div>
    );
};

export default ExpensesPanel;
